import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import useContacts from "../hooks/useContacts";
import { AUTH_STORAGE_KEY, connectSocket, SERVER_URL } from "../socket";
import CreateGroupModal from "./CreateGroupModal";
import GroupInfoPanel from "../components/GroupInfoPanel";
import ImageEditor from "../components/ImageEditor";
import EmojiPicker from "emoji-picker-react";
import ContactInfoPanel from "../components/ContactInfoPanel";
import CreateChannelModal from "./CreateChannelModal"; // add this line

import ForwardModal from "../components/ForwardModal";
import useCallHistory from "../hooks/useCallHistory";
import { FiPhone, FiVideo, FiPhoneIncoming, FiPhoneMissed, FiPhoneOff } from "react-icons/fi";

const isLocationText = (text) => {
  if (!text || typeof text !== "string") return false;
  try {
    const d = JSON.parse(text);
    return typeof d.latitude === "number" && typeof d.longitude === "number";
  } catch { return false; }
};

function ManualMediaItem({ url, type }) {
  const [revealed, setRevealed] = useState(false);
  if (revealed) {
    return <img src={url} className="chat-image" alt="media" />;
  }

  return (
    <div
      onClick={() => setRevealed(true)}
      style={{
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        gap: 6, padding: "18px 24px",
        background: "#f1f5f9", borderRadius: 10,
        cursor: "pointer", minWidth: 140,
        border: "1px solid #e2e8f0",
      }}
    >
      <span style={{ fontSize: 28 }}>🖼️</span>
      <span style={{ fontSize: 12, color: "#1e40af", fontWeight: 500 }}>Tap to download</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   EDIT MESSAGE MODAL  (WhatsApp-style)
───────────────────────────────────────────── */
function EditMessageModal({ msg, editingText, setEditingText, onConfirm, onClose }) {
  const [showEmoji, setShowEmoji] = useState(false);

  if (!msg) return null;

  const timeLabel = msg.createdAt || msg.time
    ? new Date(msg.createdAt || msg.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.52)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(92vw, 390px)",
          background: "#fff",
          borderRadius: 18,
          overflow: "visible",
          boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 18px",
          borderBottom: "1px solid #f1f5f9",
        }}>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: 19, color: "#64748b", padding: 0, lineHeight: 1,
              display: "flex", alignItems: "center",
            }}
          >
            ✕
          </button>
          <span style={{ fontSize: 15, fontWeight: 600, color: "#1e293b" }}>
            Edit message
          </span>
        </div>

        {/* ── Message preview ── */}
        <div style={{
          padding: "18px 16px",
          background: "#e5ddd5",
          display: "flex",
          justifyContent: "flex-end",
        }}>
          <div style={{
            background: "#dcf8c6",
            padding: "9px 13px",
            borderRadius: "12px 12px 0 12px",
            maxWidth: "82%",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
          }}>
            <p style={{ margin: 0, fontSize: 14, color: "#111", lineHeight: 1.55 }}>
              {msg.text}
            </p>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              gap: 4, marginTop: 4,
            }}>
              <span style={{ fontSize: 11, color: "#7a9278" }}>{timeLabel}</span>
              <span style={{ fontSize: 13, color: "#53bdeb" }}>✔✔</span>
            </div>
          </div>
        </div>

        {/* ── Input row ── */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "13px 16px",
          borderTop: "1px solid #f1f5f9",
          position: "relative",
          background: "#fff",
          borderRadius: "0 0 18px 18px",
        }}>
          <input
            autoFocus
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onConfirm();
              if (e.key === "Escape") onClose();
            }}
            style={{
              flex: 1,
              border: "none",
              borderBottom: "2px solid #25d366",
              outline: "none",
              fontSize: 15,
              padding: "4px 0",
              background: "transparent",
              color: "#1e293b",
            }}
          />

          {/* Emoji toggle */}
          <button
            onClick={() => setShowEmoji((p) => !p)}
            style={{
              background: "none", border: "none",
              cursor: "pointer", fontSize: 21, color: "#64748b",
              display: "flex", alignItems: "center",
            }}
          >
            😊
          </button>

          {/* Confirm button */}
          <button
            onClick={onConfirm}
            style={{
              width: 42, height: 42, borderRadius: "50%",
              background: "#25d366", border: "none",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(37,211,102,0.35)",
            }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
          </button>

          {/* Emoji picker */}
          {showEmoji && (
            <div style={{
              position: "absolute", bottom: 62, right: 16, zIndex: 100,
            }}>
              <EmojiPicker
                onEmojiClick={(data) => {
                  setEditingText((prev) => prev + data.emoji);
                  setShowEmoji(false);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
function Krivachat({ setVideoCall, setVoiceCall, onOpenComposeModal }) {
  const location = useLocation();
  const [pinnedGroups, setPinnedGroups] = useState([]);
  const deliveredRef = useRef(new Set());
  const seenMessagesRef = useRef(new Set());
  const { contacts, addContact } = useContacts();
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [hoverMsgId, setHoverMsgId] = useState(null);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [menu, setMenu] = useState(null);
  const [currentStatus, setCurrentStatus] = useState("open");
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [conversations, setConversations] = useState([]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [deeplinkGroupId, setDeeplinkGroupId] = useState(null);
const [channels, setChannels] = useState([]);
const [selectedChannel, setSelectedChannel] = useState(null);
const [exploreChannels, setExploreChannels] = useState([]);
const [showCreateChannel, setShowCreateChannel] = useState(false);
  useEffect(() => {
    if (!location.state?.email) return;
    const email = location.state.email;

    // Try contacts first
    const found = contacts.find(c => c.emails?.[0] === email);
    if (found) {
      setSelectedContact(found);
    } else {
      // Build synthetic contact from state (works even for non-contacts)
      setSelectedContact({
        id:             email,
        name:           location.state.name || email.split("@")[0],
        emails:         [email],
        profileImageUrl: "",
      });
    }
  }, [location.state, contacts]);

  // Handle notification reply action
  useEffect(() => {
    const handleNotificationReply = (event) => {
      const { senderEmail, replyText } = event.detail || {};
      
      console.log('[Krivachat] Notification reply received:', { senderEmail, replyText });
      
      if (senderEmail) {
        // Try contacts first
        const found = contacts.find(c => c.emails?.[0] === senderEmail);
        if (found) {
          console.log('[Krivachat] Found contact:', found);
          setSelectedContact(found);
        } else {
          // Build synthetic contact
          console.log('[Krivachat] Creating synthetic contact for:', senderEmail);
          setSelectedContact({
            id:             senderEmail,
            name:           senderEmail.split("@")[0],
            emails:         [senderEmail],
            profileImageUrl: "",
          });
        }
        
        // Pre-fill the chat input with reply text if provided
        if (replyText) {
          console.log('[Krivachat] Pre-filling chat input with:', replyText);
          setChatInput(replyText);
        }
        
        // Clear the stored notification data
        sessionStorage.removeItem('notification_reply_sender');
      }
    };

    // Check if there's stored notification data on component mount
    const storedReply = sessionStorage.getItem('notification_reply_sender');
    if (storedReply) {
      try {
        const parsed = JSON.parse(storedReply);
        console.log('[Krivachat] Found stored notification reply:', parsed);
        handleNotificationReply({
          detail: { senderEmail: parsed.email, replyText: parsed.replyText }
        });
      } catch (e) {
        console.error('[Krivachat] Error parsing stored reply:', e);
      }
    }

    window.addEventListener('notification-reply-action', handleNotificationReply);
    return () => {
      window.removeEventListener('notification-reply-action', handleNotificationReply);
    };
  }, [contacts]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const groupParam = params.get("group") || location.state?.groupId;
    if (!groupParam) return;
    setDeeplinkGroupId(groupParam);
  }, [location.search, location.state]);

  useEffect(() => {
    if (!deeplinkGroupId || !groups.length) return;
    const targetGroup = groups.find(g => String(g.id) === String(deeplinkGroupId));
    if (targetGroup) {
      setSelectedGroup(targetGroup);
      setSelectedContact(null);
    }
  }, [deeplinkGroupId, groups]);
 
  const [reactionPopup, setReactionPopup] = useState(null);
  const [downloadMode, setDownloadMode] = useState("manual");
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [caption, setCaption] = useState("");
  const messageRefs = useRef({});
  // const [accepting, setAccepting] = useState(null);
  // const [connectRequests, setConnectRequests] = useState([]);
  // const connectCount = connectRequests.length;

  const [highlightMsg, setHighlightMsg] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [favoriteContacts, setFavoriteContacts] = useState([]);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [locationSharing, setLocationSharing] = useState(false);
  const locationWatchRef = useRef(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardSearch, setForwardSearch] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [forwarding, setForwarding] = useState(false);
const [activePreview, setActivePreview] = useState(0);

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed?.user?.email?.toLowerCase() || null;
    } catch { return null; }
  });

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);
  const parseArrayResponse = async (response) => {
    try {
      const data = await response.json();
      return normalizeArray(data);
    } catch {
      return [];
    }
  };

  const [onlineUsers, setOnlineUsers] = useState({});
  const [typingUser, setTypingUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [replyMsg, setReplyMsg] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [unreadMap, setUnreadMap] = useState({});
  const [members, setMembers] = useState([]);
  const socketRef = useRef(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeout = useRef(null);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [onlyAdmin, setOnlyAdmin] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [statuses, setStatuses] = useState([]);
  const [currentStatusUser, setCurrentStatusUser] = useState(null);
  const [statusIndex, setStatusIndex] = useState(0);
  const [showAddContact, setShowAddContact] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [chatStatusMap, setChatStatusMap] = useState({});

  const { callHistory, loadingCalls, loadCallHistory, saveCall, deleteCall, clearAllCalls } = useCallHistory();
  const callStartRef = useRef(null);

  useEffect(() => {
    if (activeTab === "calls") loadCallHistory();
  }, [activeTab]);

  const [statusSummary, setStatusSummary] = useState({ open: 0, progress: 0, action: 0, closed: 0 });
  const [contactForm, setContactForm] = useState({ name: "", emails: [] });
  const [emailTyping, setEmailTyping] = useState("");
  const [showMention, setShowMention] = useState(false);
  const [mentionList, setMentionList] = useState([]);
  const [mentionQuery, setMentionQuery] = useState("");
  const [viewers, setViewers] = useState([]);
  const [showStatusViewer, setShowStatusViewer] = useState(false);
  const [showViewersPanel, setShowViewersPanel] = useState(false);
  const [statusViewCounts, setStatusViewCounts] = useState({});
  const [showSidebar, setShowSidebar] = useState(true);
  const [pinnedContacts, setPinnedContacts] = useState([]);
  const [archivedContacts, setArchivedContacts] = useState([]);
  const selectedContactRef = useRef(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [quickRepliesMatches, setQuickRepliesMatches] = useState([]);
  const selectedGroupRef = useRef(null);
  const statusInputRef = useRef(null);
  const profileEmail = profileUser?.userId || selectedContact?.emails?.[0];
  const isInContacts = contacts.some((c) => {
    const emails = Array.isArray(c.emails) ? c.emails : [c.email || c.contactId || c.id];
    return emails.some((email) => String(email).toLowerCase() === String(profileEmail || "").toLowerCase());
  });
  useEffect(() => { selectedContactRef.current = selectedContact; }, [selectedContact]);
  useEffect(() => { selectedGroupRef.current = selectedGroup; }, [selectedGroup]);

  const filteredContacts = contacts.filter(c => {
    const name = (c.name || "").toLowerCase();
    const email = (c.emails?.[0] || "").toLowerCase();
    const search = contactSearch.toLowerCase();
    const matchesSearch = name.includes(search) || email.includes(search);
    if (!matchesSearch) return false;
    if (statusFilter === "all") return true;
    const status = chatStatusMap[email] || "open";
    return status === statusFilter;
  });
useEffect(() => {
  const fetchChannels = async () => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/channels`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      setChannels(await parseArrayResponse(res));
    } catch {
      setChannels([]);
    }
  };
  fetchChannels();
}, []);

useEffect(() => {
  const socket = socketRef.current;
  if (!socket || !selectedChannel) return;
  socket.emit("join_channel", selectedChannel.id);
}, [selectedChannel]);

useEffect(() => {
  const socket = socketRef.current;
  if (!socket) return;
  const onChannelMsg = (msg) => {
    if (String(msg.channelId) === String(selectedChannel?.id)) {
      setChatMessages(prev => [...prev, { ...msg, isOwn: msg.sender === currentUser, status: "sent" }]);
    } else {
      setUnreadMap(prev => ({ ...prev, [`channel:${msg.channelId}`]: (prev[`channel:${msg.channelId}`] || 0) + 1 }));
    }
  };
  socket.on("channel_message", onChannelMsg);
  return () => socket.off("channel_message", onChannelMsg);
}, [selectedChannel, currentUser]);

useEffect(() => {
  if (!selectedChannel) return;
  const load = async () => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const res = await fetch(`${SERVER_URL}/api/channels/${selectedChannel.id}/messages`, { headers: { Authorization: `Bearer ${parsed.token}` } });
    const body = await res.json();
    setChatMessages(body.map(m => ({ ...m, isOwn: m.sender === currentUser, status: "sent" })));
  };
  load();
}, [selectedChannel]);


const handleSendChannelMessage = async () => {
  if (!chatInput.trim() || selectedChannel.myRole !== "admin") return;
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  const parsed = JSON.parse(raw);
  await fetch(`${SERVER_URL}/api/channels/${selectedChannel.id}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
    body: JSON.stringify({ text: chatInput, type: "text" }),
  });
  setChatInput("");
};

  useEffect(() => {
    const loadQR = async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed?.token) return;
        const res = await fetch(`${SERVER_URL}/api/settings/quick-replies`, {
          headers: { Authorization: `Bearer ${parsed.token}` },
        });
        const data = await res.json();
        setQuickReplies(data?.quickReplies || []);
      } catch (_) {}
    };
    loadQR();
    const handler = (e) => setQuickReplies(e.detail || []);
    window.addEventListener("quick-replies-updated", handler);
    return () => window.removeEventListener("quick-replies-updated", handler);
  }, []);

  useEffect(() => {
    window.__saveCall = saveCall;
    return () => { delete window.__saveCall; };
  }, [saveCall]);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const socket = socketRef.current;
  //     if (!socket) return;
  //     const handler = (data) => {
  //       setConnectRequests(prev => {
  //         const exists = prev.find(p => p.fromUser === data.fromUser);
  //         if (exists) return prev;
  //         return [{ id: Date.now(), fromUser: data.fromUser }, ...prev];
  //       });
  //     };
  //     socket.on("new_contact_request", handler);
  //     clearInterval(interval);
  //   }, 500);
  // }, []);

  const loadConversations = async () => {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const res = await fetch(`${SERVER_URL}/api/conversations`, {
      headers: { Authorization: `Bearer ${parsed.token}` }
    });
    const data = await res.json();
    setConversations(data.conversations || []);
  } catch (_) {}
};

// Load on mount and when messages arrive
useEffect(() => { if (currentUser) loadConversations(); }, [currentUser]);

  const handleSendLocation = async () => {
    if (!selectedContact && !selectedGroup) return;
    if (!navigator.geolocation) { alert("Geolocation is not supported by your browser."); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        await sendLocationMessage(latitude, longitude, false);
      },
      () => alert("Location access denied.")
    );
  };

  const handleToggleLiveLocation = () => {
    if (!navigator.geolocation) { alert("Geolocation not supported."); return; }
    if (locationSharing) {
      if (locationWatchRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchRef.current);
        locationWatchRef.current = null;
      }
      setLocationSharing(false);
      return;
    }
    setLocationSharing(true);
    locationWatchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        await sendLocationMessage(latitude, longitude, true);
      },
      () => { setLocationSharing(false); alert("Could not get location."); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
  };

  const sendLocationMessage = async (latitude, longitude, isLive) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const locationData = JSON.stringify({ latitude, longitude, isLive });

    if (selectedGroup) {
      const res = await fetch(`${SERVER_URL}/api/groups/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
        body: JSON.stringify({ groupId: selectedGroup.id, text: locationData, type: "location" }),
      });
      const data = await res.json();
      setChatMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, { ...data, type: "location", isOwn: true, status: "sent" }];
      });
      return;
    }

    const other = selectedContact.emails[0];
    const payload = {
      from: currentUser, to: other, text: locationData,
      type: "location", statusTag: currentStatus, time: Date.now(),
    };
    const res = await fetch(`${SERVER_URL}/save-message`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    payload.id = data.id;
    if (socketRef.current) socketRef.current.emit("chat_message", { ...payload, id: data.id });
    setChatMessages(prev => [...prev, { ...payload, id: data.id, status: "sent", isOwn: true }]);
  };

  useEffect(() => {
    return () => {
      if (locationWatchRef.current !== null) navigator.geolocation.clearWatch(locationWatchRef.current);
    };
  }, []);

  useEffect(() => {
    if (locationWatchRef.current !== null) {
      navigator.geolocation.clearWatch(locationWatchRef.current);
      locationWatchRef.current = null;
    }
    setLocationSharing(false);
  }, [selectedContact, selectedGroup]);

  const handleForward = async (data) => {
    const { contacts: toContacts = [], groups: toGroups = [] } = data || {};
    if (!forwardMsg || (!toContacts?.length && !toGroups?.length)) return;
    
    setForwarding(true);
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    
    // Forward to individual contacts
    for (const toContact of toContacts) {
      const other = toContact.emails?.[0];
      if (!other) continue;
      const payload = {
        from: currentUser, to: other, text: forwardMsg.text || "",
        type: "text", statusTag: "open",
        forwardedFrom: forwardMsg.from, forwardedMessageId: forwardMsg.id, time: Date.now(),
      };
      try {
        const res = await fetch(`${SERVER_URL}/save-message`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        payload.id = data.id;
        if (socketRef.current) socketRef.current.emit("chat_message", { ...payload, id: data.id, from: currentUser, to: other });
        if (selectedContact?.emails?.[0] === other) {
          setChatMessages(prev => [...prev, { ...payload, id: data.id, status: "sent", isOwn: true }]);
        }
      } catch (err) { console.error(`Forward to ${other} failed`, err); }
    }
    
    // Forward to groups
    for (const toGroup of toGroups) {
      const groupId = toGroup.id;
      if (!groupId) continue;
      const payload = {
        groupId: groupId,
        sender: currentUser,
        text: forwardMsg.text || "",
        type: "text",
        forwardedFrom: forwardMsg.from,
        forwardedMessageId: forwardMsg.id,
        time: Date.now(),
      };
      try {
        const res = await fetch(`${SERVER_URL}/api/groups/message`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
          body: JSON.stringify(payload),
        });
        const msgData = await res.json();
        if (socketRef.current) {
          socketRef.current.emit("group_message", { ...payload, id: msgData.id });
        }
      } catch (err) { console.error(`Forward to group ${groupId} failed`, err); }
    }
    
    setForwarding(false);
    setShowForwardModal(false);
    setForwardMsg(null);
  };

  // const handleReject = async (req) => {
  //   const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  //   const parsed = JSON.parse(raw);
  //   await fetch(`${SERVER_URL}/api/contact-requests/reject`, {
  //     method: "POST",
  //     headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
  //     body: JSON.stringify({ id: req.id }),
  //   });
  //   setConnectRequests(prev => prev.filter(r => r.id !== req.id));
  // };

  // const loadConnectRequests = async () => {
  //   const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  //   const parsed = JSON.parse(raw);
  //   const res = await fetch(`${SERVER_URL}/api/contact-requests`, {
  //     headers: { Authorization: `Bearer ${parsed.token}` },
  //   });
  //   const data = await res.json();
  //   setConnectRequests(data);
  // };

  useEffect(() => {
    const loadPrivacy = async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (!parsed?.token) return;
        const res = await fetch(`${SERVER_URL}/api/settings/privacy`, {
          headers: { Authorization: `Bearer ${parsed.token}` },
        });
        const data = await res.json();
        if (data?.settings?.downloadMode) setDownloadMode(data.settings.downloadMode);
      } catch (_) {}
    };
    loadPrivacy();
  }, []);

  //useEffect(() => { loadConnectRequests(); }, []);

  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    if (filePreviews.length > 0) setCaption(prev => prev + emoji);
    else setChatInput(prev => prev + emoji);
  };

  const canEditOrDelete = (msg) => {
    const messageTime = new Date(msg.createdAt || msg.time).getTime();
    const now = Date.now();
    const diffMinutes = (now - messageTime) / (1000 * 60);
    return diffMinutes <= 10;
  };

  const allContacts = filteredContacts;
  const unreadContacts = filteredContacts.filter(c => (unreadMap[String(c.emails?.[0]).toLowerCase()] || 0) > 0);
  const unreadGroups = groups.filter(group => (unreadMap[`group:${group.id}`] || 0) > 0);
  const groupContacts = groups;

  const isAdmin =
    members?.length > 0 &&
    members.some(m => m.userId?.toLowerCase() === currentUser?.toLowerCase() && m.role === "admin");
  const isRestricted = onlyAdmin && !isAdmin;
const isChannelRestricted = selectedChannel && selectedChannel.myRole !== "admin";

  useEffect(() => {
    const closeMenu = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      const isMenu = target.closest(".message-menu");
      const isTrigger = target.closest(".menu-btn, .icon-btn, .sidebar-actions, .header-menu-wrapper");

      if (isMenu || isTrigger) return;
      setMenu(null);
    };

    document.addEventListener("pointerdown", closeMenu);
    return () => document.removeEventListener("pointerdown", closeMenu);
  }, []);

  const handleSelectQuickReply = (reply) => {
    setChatInput(reply.message);
    setShowQuickReplies(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".attach-popup") && !e.target.closest(".plus-icon")) setShowAttachMenu(false);
      if (!e.target.closest(".emoji-picker-box") && !e.target.closest(".pill-icon") && !e.target.closest(".input-icon")) setShowEmoji(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const currentUserRef = useRef(currentUser);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  useEffect(() => {
    const loadAllStatuses = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/chat/status/all`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const data = await res.json();
      const map = {};
      data.forEach(row => {
        const other = row.user_min === currentUser ? row.user_max : row.user_min;
        map[other] = row.status;
      });
      setChatStatusMap(map);
    };
    if (currentUser) loadAllStatuses();
  }, [currentUser]);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = JSON.parse(raw);
        const res = await fetch(`${SERVER_URL}/api/chat/status/summary`, { headers: { Authorization: `Bearer ${parsed.token}` } });
        const data = await res.json();
        setStatusSummary({ open: data.open || 0, progress: data.progress || 0, action: data.action || 0, closed: data.closed || 0 });
      } catch (err) { console.error("Summary error:", err); }
    };
    loadSummary();
  }, []);

const formatTime = (value) => {
  if (!value) return "";

  let date;

  if (!isNaN(Number(value))) {
    date = new Date(Number(value));
  } else {
    date = new Date(value);
  }

  if (isNaN(date.getTime())) {
    console.log("Invalid Time:", value);
    return "";
  }

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};
useEffect(() => {
  const fetchExploreChannels = async () => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/channels/explore`, {
        headers: { Authorization: `Bearer ${parsed.token}` },
      });
      const data = await res.json();
      // Handle either a bare array or a {channels: [...]} wrapper
      const list = Array.isArray(data) ? data : (data.channels || []);
      setExploreChannels(list);
    } catch (err) {
      console.error("Failed to load explore channels", err);
      setExploreChannels([]);
    }
  };
  fetchExploreChannels();
}, []);
  useEffect(() => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const email = parsed?.user?.email?.toLowerCase();
    setCurrentUser(email);
    if (!socketRef.current) socketRef.current = connectSocket(parsed?.token);
    const socket = socketRef.current;
    socket.emit("join", email);
    socket.on("online_users", ({ emails }) => {
      setOnlineUsers(prev => {
        const next = { ...prev };
        (emails || []).forEach((email) => {
          if (email) next[String(email).toLowerCase()] = true;
        });
        return next;
      });
    });
    socket.on("user_online", ({ email }) => {
      const normalized = String(email || "").toLowerCase();
      setOnlineUsers(prev => ({ ...prev, [normalized]: true }));
    });
    socket.on("user_offline", ({ email }) => {
      const normalized = String(email || "").toLowerCase();
      setOnlineUsers(prev => ({ ...prev, [normalized]: false }));
    });
    return () => {
      socket.off("online_users");
      socket.off("user_online");
      socket.off("user_offline");
    };
  }, []);

  useEffect(() => {
    if (!selectedContact) return;
    const loadStatus = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/chat/status?user=${selectedContact.emails[0]}`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const data = await res.json();
      setCurrentStatus(data.status || "open");
    };
    loadStatus();
  }, [selectedContact]);

  useEffect(() => {
    const loadExtra = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const pinRes = await fetch(`${SERVER_URL}/api/pin-contact`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const pins = await pinRes.json();
      const pinnedUsers = pins.filter(x => !x.startsWith("group:"));

const pinnedGroups = pins
  .filter(x => x.startsWith("group:"))
  .map(x => x.replace("group:", ""));

setPinnedContacts(pinnedUsers);
setPinnedGroups(pinnedGroups);
      const archRes = await fetch(`${SERVER_URL}/api/archive-contact`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      //setPinnedContacts(await pinRes.json());
      setArchivedContacts(await archRes.json());
    };
    loadExtra();
  }, []);

  const handleStatusChange = async (newStatus) => {
    const oldStatus = currentStatus;
    setCurrentStatus(newStatus);
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/chat/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ user2: selectedContact.emails[0], status: newStatus }),
    });
    setStatusSummary(prev => {
      const updated = { ...prev };
      if (updated[oldStatus] > 0) updated[oldStatus]--;
      updated[newStatus]++;
      return updated;
    });
    setChatStatusMap(prev => ({ ...prev, [selectedContact.emails[0]]: newStatus }));
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    if (!contactForm.emails.length) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/contacts`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ contactId: contactForm.emails[0], alias: contactForm.name }),
    });
    alert("Contact Added ✅");
    setShowAddContact(false);
    setContactForm({ name: "", emails: [] });
  };

  const handlePin = async (email) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/pin-contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ contact: email }),
    });
    setPinnedContacts(prev => prev.includes(email) ? prev.filter(x => x !== email) : [...prev, email]);
  };

  const handleArchive = async (email) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/archive-contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ contact: email }),
    });
    setArchivedContacts(prev => prev.includes(email) ? prev.filter(x => x !== email) : [...prev, email]);
  };

  useEffect(() => {
    const loadFavContacts = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/favorite-contact`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const data = await res.json();
      setFavoriteContacts(data);
    };
    loadFavContacts();
  }, []);

  const handleSelectMention = (contact) => {
    const name = contact.name || contact.emails[0];
    const newText = chatInput.replace(/@\w*$/, `@${name} `);
    setChatInput(newText);
    setShowMention(false);
  };

  useEffect(() => {
    const fetchGroups = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/groups`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const body = await res.json();
      setGroups(body || []);
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    const loadStatus = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/status`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const data = await res.json();
      setStatuses(Array.isArray(data) ? data : []);
      await loadStatusViewCounts(Array.isArray(data) ? data : []);
    };
    loadStatus();
  }, []);

  // Listen for group deletion notifications
  useEffect(() => {
    const handleGroupDeleted = (event) => {
      const { groupId } = event.detail;
      setGroups(prev => prev.filter(g => g.id !== groupId));
      // Close group info if the deleted group is currently selected
      if (selectedGroup && selectedGroup.id === groupId) {
        setShowGroupInfo(false);
        setSelectedGroup(null);
      }
    };

    window.addEventListener("group-deleted", handleGroupDeleted);
    return () => window.removeEventListener("group-deleted", handleGroupDeleted);
  }, [selectedGroup]);

  const handleFavoriteContact = async (contactEmail) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/favorite-contact`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ contact: contactEmail }),
    });
    setFavoriteContacts(prev => prev.includes(contactEmail) ? prev.filter(c => c !== contactEmail) : [...prev, contactEmail]);
  };

  const handleClearChat = async () => {
    if (!selectedContact) return;
    const confirmClear = window.confirm("Clear all messages?");
    if (!confirmClear) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/messages/clear`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ user: selectedContact.emails[0] }),
    });
    setChatMessages([]);
    setMenu(null);
  };

  const handleUnblockUser = async () => {
    if (!selectedContact) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/block`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ blockedUser: selectedContact.emails[0] }),
    });
    alert("User unblocked");
    setIsBlocked(false);
    setMenu(null);
  };

  const handleCreateGroup = async (name, members, groupImage) => {
    if (!name.trim()) { alert("Group name required"); return; }
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
        body: JSON.stringify({ name, members, groupImage }),
      });
      const data = await res.json();
      setGroups(prev => [...prev, data]);
      setShowCreateGroup(false);
    } catch (err) { console.log("Group create failed", err); }
  };

  const checkBlocked = async () => {
    if (!selectedContact) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const res = await fetch(`${SERVER_URL}/api/block/check?user=${selectedContact.emails[0]}`, {
      headers: { Authorization: `Bearer ${parsed.token}` },
    });
    const data = await res.json();
    setIsBlocked(data.blocked || data.blockedBy);
  };

  useEffect(() => { if (selectedContact) checkBlocked(); }, [selectedContact]);

  const fetchUserProfile = async (email) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const res = await fetch(`${SERVER_URL}/api/users/profile?email=${email}`, { headers: { Authorization: `Bearer ${parsed.token}` } });
    const data = await res.json();
    setProfileUser(data);
  };

  const handleBlockUser = async () => {
    if (!selectedContact) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/block`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ blockedUser: selectedContact.emails[0] }),
    });
    alert("User blocked");
    setMenu(null);
  };

  const handleCopyMessage = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => console.log("Copied!")).catch(() => alert("Copy failed"));
  };

  const scrollToMessage = (id) => {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightMsg(id);
    setTimeout(() => setHighlightMsg(null), 2000);
  };

  const highlightText = (text, search) => {
    if (!search) return text;
    const parts = text.split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === search.toLowerCase() ? <mark key={i}>{part}</mark> : part
    );
  };

  const uploadStatus = async (file) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${SERVER_URL}/api/status`, {
      method: "POST",
      headers: { Authorization: `Bearer ${parsed.token}` },
      body: formData,
    });
    const data = await res.json();
    setStatuses(prev => [data, ...prev]);
  };

  const deleteStatus = async (statusId) => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/status/${statusId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${parsed.token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      setStatuses(prev => prev.filter(s => s.id !== statusId));
      setShowStatusViewer(false);
    } catch (err) { console.log("Status delete error:", err); alert("Failed to delete status"); }
  };

  const handleReaction = async (id, reaction) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/messages/react`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ id, reaction }),
    });
    setChatMessages(prev => prev.map(m => m.id !== id ? m : { ...m, reaction }));
  };

  const groupedStatuses = Object.values(
    statuses.reduce((acc, s) => {
      if (!acc[s.userId]) acc[s.userId] = { userId: s.userId, statuses: [] };
      acc[s.userId].statuses.push(s);
      return acc;
    }, {})
  );

  const handleRightClick = (e, msg) => {
    e.preventDefault();
    setMenu({
      type: "message",
      x: Math.min(e.pageX, window.innerWidth - 150),
      y: Math.min(e.pageY, window.innerHeight - 200),
      message: msg,
    });
  };

  const openStatus = async (user) => {
    if (!user?.statuses?.length) return;
    setCurrentStatusUser(user);
    setStatusIndex(0);
    setShowStatusViewer(true);
    setShowViewersPanel(false);
    setViewers([]);
    const firstStatus = user.statuses[0];
    try {
      await recordStatusView(firstStatus.id);
      await loadViews(firstStatus.id);
    } catch (err) {
      console.error("Failed to open status viewer", err);
    }
  };

  const normalizeViewers = (items = []) => {
    const unique = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      const key = item.viewerId || item.User?.userId || item.id;
      if (!key || unique.has(key)) return;
      unique.set(key, item);
    });
    return Array.from(unique.values());
  };

  const loadViews = async (statusId) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const res = await fetch(`${SERVER_URL}/api/status/views/${statusId}`, { headers: { Authorization: `Bearer ${parsed.token}` } });
    const data = await res.json();
    const uniqueViewers = normalizeViewers(data);
    setViewers(uniqueViewers);
    setStatusViewCounts(prev => ({ ...prev, [statusId]: uniqueViewers.length }));
    return uniqueViewers;
  };

  const recordStatusView = async (statusId) => {
    if (!statusId) return null;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    try {
      await fetch(`${SERVER_URL}/api/status/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
        body: JSON.stringify({ statusId }),
      });
      return await loadViews(statusId);
    } catch (err) {
      console.error("Failed to record status view", err);
      return null;
    }
  };

  const loadStatusViewCounts = async (items = []) => {
    const statusIds = Array.from(new Set((items || []).map(item => item.id).filter(Boolean)));
    if (!statusIds.length) return;

    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const results = await Promise.all(
      statusIds.map(async (statusId) => {
        try {
          const res = await fetch(`${SERVER_URL}/api/status/views/${statusId}`, {
            headers: { Authorization: `Bearer ${parsed.token}` },
          });
          const data = await res.json();
          const uniqueCount = normalizeViewers(data).length;
          return [statusId, uniqueCount];
        } catch {
          return [statusId, 0];
        }
      })
    );

    setStatusViewCounts(prev => results.reduce((acc, [statusId, count]) => ({ ...acc, [statusId]: count }), prev));
  };

  useEffect(() => {
    if (!currentStatusUser) return;
    const timer = setTimeout(() => {
      if (statusIndex < currentStatusUser.statuses.length - 1) setStatusIndex(prev => prev + 1);
      else setShowStatusViewer(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, [statusIndex, currentStatusUser]);

  useEffect(() => {
    if (!showStatusViewer || !currentStatusUser || !currentStatusUser.statuses?.length) return;
    const currentStatus = currentStatusUser.statuses[statusIndex];
    if (!currentStatus?.id) return;
    loadViews(currentStatus.id);
  }, [showStatusViewer, currentStatusUser, statusIndex]);

  /* ── Edit message (called from modal confirm) ── */
  const handleEditMessage = async (id) => {
    if (!editingText.trim()) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/messages/edit`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ id, text: editingText }),
    });
    setChatMessages(prev => prev.map(m => m.id === id ? { ...m, text: editingText } : m));
    setEditingMsgId(null);
    setEditingText("");
    setMenu(null);
  };

  const handleDeleteForEveryone = async (id) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/messages/delete-everyone`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
      body: JSON.stringify({ id }),
    });
    setChatMessages(prev => prev.map(m => m.id === id ? { ...m, deleted: true } : m));
    setMenu(null);
  };

  const handleDeleteMessage = async (id) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/messages/delete/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${parsed.token}` },
    });
    setChatMessages(prev => prev.filter(m => m.id !== id));
    setMenu(null);
  };

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !selectedGroup) return;
    const joinGroup = () => socket.emit("join_group", selectedGroup.id);
    joinGroup();
    socket.on("connect", joinGroup);
    return () => socket.off("connect", joinGroup);
  }, [selectedGroup]);

  useEffect(() => {
    if (!selectedGroup) return;
    const loadGroup = async () => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/groups/${selectedGroup.id}`, { headers: { Authorization: `Bearer ${parsed.token}` } });
      const body = await res.json();
      setOnlyAdmin(body.onlyAdmin || false);
      setMembers(body.members || []);
    };
    loadGroup();
  }, [selectedGroup]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!currentUser) return;
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      if (selectedGroup) {
        const res = await fetch(`${SERVER_URL}/api/groups/${selectedGroup.id}/messages`, { headers: { Authorization: `Bearer ${parsed.token}` } });
        const body = await res.json();
        setChatMessages((body || []).map(m => ({
          ...m,
          type: m.type || "text",
          url: m.url || m.attachmentUrl || null,
          isOwn: m.sender === currentUser,
          status: m.sender === currentUser ? "sent" : undefined,
        })));
        return;
      }
      if (selectedContact) {
        const other = selectedContact.emails[0];
        const res = await fetch(`${SERVER_URL}/api/messages/history?with=${other}`, { headers: { Authorization: `Bearer ${parsed.token}` } });
        const body = await res.json();
        setChatMessages((body.messages || []).map(m => ({ ...m, reaction: m.reaction || null, status: m.status || "sent", isOwn: m.from === currentUser })));
      }
    };
    loadHistory();
  }, [selectedContact, selectedGroup, currentUser]);

  useEffect(() => {
    chatMessages.forEach(m => {
      if (!m.isOwn && m.status === "sent" && m.id && m.id.length === 36 && !deliveredRef.current.has(m.id)) {
        deliveredRef.current.add(m.id);
        setTimeout(() => {
          socketRef.current.emit("message_delivered", { messageId: m.id, to: m.from });
        }, 500);
      }
    });
  }, [chatMessages, selectedContact]);

  useEffect(() => {
    if (!socketRef.current) return;
    const socket = socketRef.current;

    const onChatMessage = (msg) => {
      const contact = selectedContactRef.current;
      loadConversations();
      const fromKey = String(msg.from || "").toLowerCase();
      // Case-insensitive email comparison
      const msgFrom = String(msg.from || "").toLowerCase();
      const contactEmail = String(contact?.emails?.[0] || "").toLowerCase();
      if (msgFrom !== contactEmail) {
        setUnreadMap(prev => ({ ...prev, [fromKey]: (prev[fromKey] || 0) + 1 }));
        return;
      }
      setChatMessages(prev => {
        if (prev.some(m => m.id && m.id === msg.id)) return prev;
        return [...prev, { ...msg, isOwn: false, type: msg.type || "text" }];
      });
    };

    const onGroupMessage = (msg) => {
      const group = selectedGroupRef.current;
      if ((!msg.text || !msg.text.trim()) && !msg.url && !msg.attachmentUrl) return;
      if (String(msg.groupId) !== String(group?.id)) {
        setUnreadMap(prev => ({
          ...prev,
          [`group:${msg.groupId}`]: (prev[`group:${msg.groupId}`] || 0) + 1,
        }));
        loadConversations();
        return;
      }
      const safeMsg = {
        ...msg,
        id: msg.id || crypto.randomUUID(),
        type: msg.type || "text",
        isOwn: msg.sender === currentUserRef.current,
        status: msg.sender === currentUserRef.current ? "sent" : undefined,
      };
      setChatMessages(prev => prev.some(m => m.id === safeMsg.id) ? prev : [...prev, safeMsg]);
    };

    const onMessageStatus = (data) => setChatMessages(prev => prev.map(m => m.id === data.id ? { ...m, status: data.status } : m));
    const onGroupMessageStatus = (data) => setChatMessages(prev => prev.map(m => m.id === data.id ? { ...m, status: data.status } : m));
    const onMessageReaction = (data) => setChatMessages(prev => prev.map(m => m.id === data.id ? { ...m, reaction: data.reaction } : m));

    const onTyping = (data) => {
      const contact = selectedContactRef.current;
      if (data.from?.toLowerCase() === contact?.emails?.[0]?.toLowerCase()) {
        setTypingUser(data.from);
        clearTimeout(typingTimeout.current);
        typingTimeout.current = setTimeout(() => setTypingUser(null), 2000);
      }
    };
    const onStopTyping = () => setTypingUser(null);

    socket.on("chat_message", onChatMessage);
    socket.on("group_message", onGroupMessage);
    socket.on("group_message_status", onGroupMessageStatus);
    socket.on("message_status", onMessageStatus);
    socket.on("message_reaction", onMessageReaction);
    socket.on("typing", onTyping);
    socket.on("stop_typing", onStopTyping);

    return () => {
      socket.off("chat_message", onChatMessage);
      socket.off("group_message", onGroupMessage);
      socket.off("group_message_status", onGroupMessageStatus);
      socket.off("message_status", onMessageStatus);
      socket.off("message_reaction", onMessageReaction);
      socket.off("typing", onTyping);
      socket.off("stop_typing", onStopTyping);
    };
  }, [currentUser]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  useEffect(() => {
    if (!selectedContact) return;
    chatMessages.forEach(m => {
      if (!m.isOwn && m.status !== "seen" && m.id && m.id.length === 36 && !seenMessagesRef.current.has(m.id)) {
        seenMessagesRef.current.add(m.id);
        setTimeout(() => {
          socketRef.current.emit("message_seen", { messageId: m.id, to: m.from });
        }, 300);
      }
    });
  }, [selectedContact, chatMessages]);

  useEffect(() => {
    seenMessagesRef.current.clear();
    deliveredRef.current.clear();
  }, [selectedContact]);

  const handleSend = async () => {
    if (isBlocked) { alert("You can't send message"); return; }
    if (onlyAdmin && !isAdmin) { alert("🔒 Only admins can send messages"); return; }
    if (!chatInput.trim()) return;
    
    
    
    // Validate auth
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      console.error('[Krivachat] Cannot send: no auth token');
      alert("Authentication error. Please log in again.");
      return;
    }
    
    const parsed = JSON.parse(raw);
    if (!parsed.token) {
      console.error('[Krivachat] Cannot send: no token in auth');
      alert("Authentication error. Please log in again.");
      return;
    }
    
    if (selectedChannel) {
        return handleSendChannelMessage();
      }
    if (selectedGroup) {
      const textToSend = chatInput;
      const res = await fetch(`${SERVER_URL}/api/groups/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
        body: JSON.stringify({ groupId: selectedGroup.id, text: textToSend }),
      });
      const data = await res.json();
      if (data?.id) {
        setChatMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, {
          ...data,
          id: data.id,
          type: data.type || "text",
          isOwn: true,
          status: "sent",
        }]);
      }
      setChatInput("");
      setReplyMsg(null);
      return;
    }
// Validate selectedContact exists
    if (!selectedContact || !selectedContact.emails || !selectedContact.emails[0]) {
      console.error('[Krivachat] Cannot send: selectedContact invalid', selectedContact);
      alert("Please select a contact first");
      return;
    }
    const other = selectedContact.emails[0];
    console.log('[Krivachat] Sending message from', currentUser, 'to', other);
    
    const payload = {
      from: currentUser, to: other, text: chatInput, type: "text",
      statusTag: currentStatus, replyId: replyMsg ? replyMsg.id : null,
      replyTo: replyMsg ? replyMsg.text : null, time: Date.now(),
    };
    setChatInput("");
    
    try {
      const res = await fetch(`${SERVER_URL}/save-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      
      if (!data.id) {
        console.error('[Krivachat] Server response missing id:', data);
        alert("Failed to send message");
        return;
      }
      
      payload.id = data.id;
      if (socketRef.current) {
        console.log('[Krivachat] Emitting chat_message via socket');
        socketRef.current.emit("chat_message", { ...payload, id: data.id, from: currentUser, to: other });
      }
      setChatMessages(prev => [...prev, { ...payload, id: data.id, status: "sent", isOwn: true }]);
      setReplyMsg(null);
    } catch (err) {
      console.error('[Krivachat] Error sending message:', err);
      alert("Failed to send message: " + err.message);
    }
  };

  const startRecording = async () => {
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
    catch (err) { alert("Microphone permission denied"); return; }
    mediaRecorderRef.current = new MediaRecorder(stream);
    audioChunksRef.current = [];
    mediaRecorderRef.current.ondataavailable = (e) => audioChunksRef.current.push(e.data);
    mediaRecorderRef.current.onstop = async () => {
      const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", blob, "voice.webm");
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      if (selectedContact) formData.append("to", selectedContact.emails[0]);
      const res = await fetch(`${SERVER_URL}/api/attachments`, {
        method: "POST", headers: { Authorization: `Bearer ${parsed.token}` }, body: formData,
      });
      const body = await res.json();
      setChatMessages(prev => [...prev, { id: crypto.randomUUID(), type: "audio", url: body.url || body.attachmentUrl, isOwn: true, status: "sent" }]);
    };
    mediaRecorderRef.current.start();
    setRecording(true);
  };

  const stopRecording = () => { mediaRecorderRef.current.stop(); setRecording(false); };

//  const handleAccept = async (req) => {
//   if (accepting === req.id) return; // guard against double click
//   setAccepting(req.id);
//   try {
//     const raw = localStorage.getItem(AUTH_STORAGE_KEY);
//     const parsed = JSON.parse(raw);
//     await fetch(`${SERVER_URL}/api/contact-requests/accept`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
//       body: JSON.stringify({ id: req.id, fromUser: req.fromUser }),
//     });
//     setConnectRequests(prev => prev.filter(r => r.id !== req.id));
//   } finally {
//     setAccepting(null);
//   }
// };

  // Accepts array of { idx, markedFile, hasMarks, originalFile, caption }
  const handleMultiFileSend = async (sendObjs) => {
    if (onlyAdmin && !isAdmin) return;
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    if (Array.isArray(sendObjs)) {
      // Send each image in the array
      for (const sendObj of sendObjs) {
        const { idx, markedFile, hasMarks, originalFile, caption: sendCaption } = sendObj;
        const formData = new FormData();
        formData.append("file", hasMarks && markedFile ? markedFile : originalFile);
        if (selectedGroup) formData.append("groupId", String(selectedGroup.id));
        else formData.append("to", selectedContact.emails[0]);
        formData.append("text", sendCaption || caption || "");
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${SERVER_URL}/api/attachments`);
        xhr.setRequestHeader("Authorization", `Bearer ${parsed.token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const percent = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(prev => ({ ...prev, [idx]: percent }));
          }
        };
        xhr.onload = () => {
          const res = JSON.parse(xhr.response);
          setChatMessages(prev => {
            const messageId = res.id || crypto.randomUUID();
            if (prev.some(m => m.id === messageId)) return prev;
            return [...prev, {
              id: messageId, type: res.type || "image",
              url: res.url || res.attachmentUrl, attachmentUrl: res.attachmentUrl || res.url,
              filename: res.filename || res.attachmentName, text: sendCaption || caption || "",
              isOwn: true, status: "sent", time: Date.now(),
            }];
          });
        };
        xhr.send(formData);
      }
      setTimeout(() => { setFilePreviews([]); setUploadProgress({}); setCaption(""); setActivePreview(0); }, 500);
      return;
    }
    // Fallback: send all files (legacy multi-send)
    for (let i = 0; i < filePreviews.length; i++) {
      const item = filePreviews[i];
      const formData = new FormData();
      formData.append("file", item.file);
      if (selectedGroup) formData.append("groupId", String(selectedGroup.id));
      else formData.append("to", selectedContact.emails[0]);
      formData.append("text", caption || "");
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${SERVER_URL}/api/attachments`);
      xhr.setRequestHeader("Authorization", `Bearer ${parsed.token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded * 100) / e.total);
          setUploadProgress(prev => ({ ...prev, [i]: percent }));
        }
      };
      xhr.onload = () => {
        const res = JSON.parse(xhr.response);
        setChatMessages(prev => [...prev, {
          id: res.id || crypto.randomUUID(), type: res.type || "image",
          url: res.url || res.attachmentUrl, attachmentUrl: res.attachmentUrl || res.url,
          filename: res.filename || res.attachmentName, text: caption || "",
          isOwn: true, status: "sent", time: Date.now(),
        }]);
      };
      xhr.send(formData);
    }
    setTimeout(() => { setFilePreviews([]); setUploadProgress({}); setCaption("");setActivePreview(0);  }, 500);
  };

  const searchMessages = async (q) => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    const res = await fetch(`${SERVER_URL}/api/messages/search?q=${q}`, { headers: { Authorization: `Bearer ${parsed.token}` } });
    const data = await res.json();
    setChatMessages(data.map(m => ({ ...m, isOwn: m.from === currentUser })));
  };

  const renderContact = (contact) => {
    const email = contact.emails?.[0];
    const name = contact.name || email;
    const status = chatStatusMap[email] || "open";
    return (
      <div
        key={contact.id}
        className={`contact-item ${selectedContact?.id === contact.id ? "active" : ""}`}
        onClick={() => {
          setSelectedContact(contact);
          setSelectedGroup(null);
          setOnlyAdmin(false);
          setUnreadMap(prev => ({ ...prev, [String(contact.emails?.[0] || "").toLowerCase()]: 0 }));
          if (window.innerWidth < 768) setShowSidebar(false);
        }}
      >
        <div className="contact-avatar">
          {contact.profileImageUrl ? (
            <img src={`${SERVER_URL}${contact.profileImageUrl}`} alt="profile" className="avatar-img" />
          ) : (
            <div className="avatar-letter">{name.charAt(0).toUpperCase()}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1, minWidth: 0, gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}>
              <strong style={{ color: "var(--text-primary)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
                {name}
              </strong>
              {unreadMap[email] > 0 && <span className="unread-badge">{unreadMap[email]}</span>}
              {pinnedContacts.includes(email) && <span style={{ fontSize: 11 }}>📌</span>}
              {favoriteContacts.includes(email) && <span style={{ fontSize: 11 }}>⭐</span>}
            </div>
            <div className="contact-sub" style={{ marginTop: 3 }}>
              <span className={`status-dot ${onlineUsers[String(email || "").toLowerCase()] ? "online" : "offline"}`} />
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 140, display: "inline-block", verticalAlign: "middle" }}>
                {email}
              </span>
            </div>
          </div>
          <div style={{ flexShrink: 0 }}>
            {status === "open" && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#06a03ff5", color: "#e9f3e9", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 20, whiteSpace: "nowrap", border: "1px solid rgba(37,99,235,0.3)" }}>📂 Open</span>}
            {status === "progress" && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#1463e3", color: "#e9f3e9", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 20, whiteSpace: "nowrap", border: "1px solid rgba(245,158,11,0.3)" }}>🔄 Progress</span>}
            {status === "action" && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#c98f1f", color: "#e9f3e9", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 20, whiteSpace: "nowrap", border: "1px solid rgba(239,68,68,0.3)" }}>⚠️ Action</span>}
            {status === "closed" && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#50545a", color: "#e9f3e9", fontSize: 11, fontWeight: 700, padding: "4px 9px", borderRadius: 20, whiteSpace: "nowrap", border: "1px solid rgba(34,197,94,0.3)" }}>✅ Closed</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-layout">
      {showSidebar && (
        <div className="chat-sidebar">
          <div className="sidebar-header">
            <div className="sidebar-top">
                <div className="mail-brand-content">
          <img src="/TimeEdgeIcon.png" alt="TimeEdge Icon" className="mail-brand-icon mobilecsslogo" />
         
        </div>

             <h3 className="app-title1">
              
  Kriva <span className="chat-blue">Bharat Chat</span>
</h3>
              <div className="sidebar-actions">
                <button className="icon-btn" title="Add New Contact" onClick={() => setShowAddContact(true)}>➕</button>
                <button className="icon-btn" onClick={(e) => {
                  e.stopPropagation();
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (menu?.type === "sidebar") setMenu(null);
                  else setMenu({ type: "sidebar", x: rect.right - 140, y: rect.bottom + 5 });
                }}>⋮</button>
              </div>
            </div>
            <div className="mail-search-box">
              <input placeholder="Search name, number..." value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} />
            </div>
            <div className="status-summary-box">
              <div className={`status-card open ${statusFilter === "open" ? "active" : ""}`} onClick={() => setStatusFilter("open")}>
                <h3>{statusSummary.open}</h3><span>OPEN</span>
              </div>
               <div className={`status-card progress ${statusFilter === "progress" ? "active" : ""}`} onClick={() => setStatusFilter("progress")}>
                <h3>{statusSummary.progress}</h3><span>PROGRESS</span>
              </div>
              <div className={`status-card action ${statusFilter === "action" ? "active" : ""}`} onClick={() => setStatusFilter("action")}>
                <h3>{statusSummary.action}</h3><span>ACTION</span>
              </div>
             
              <div className={`status-card closed ${statusFilter === "closed" ? "active" : ""}`} onClick={() => setStatusFilter("closed")}>
                <h3>{statusSummary.closed}</h3><span>CLOSED</span>
              </div>
            </div>
            <div className="chat-tabs">
              <span className={activeTab === "status" ? "active" : ""} onClick={() => setActiveTab("status")}>Status</span>
              <span className={activeTab === "all" ? "active" : ""} onClick={() => setActiveTab("all")}>All</span>
              <span className={activeTab === "unread" ? "active" : ""} onClick={() => setActiveTab("unread")}>Unread</span>
              <span className={activeTab === "favs" ? "active" : ""} onClick={() => setActiveTab("favs")}>Favorites</span>
              <span className={activeTab === "archive" ? "active" : ""} onClick={() => setActiveTab("archive")}>Archive</span>
              <span className={activeTab === "groups" ? "active" : ""} onClick={() => setActiveTab("groups")}>Groups</span>
              <span className={activeTab === "channels" ? "active" : ""} onClick={() => setActiveTab("channels")}>Channels</span>
              {/* <span className={activeTab === "connect" ? "active" : ""} onClick={() => setActiveTab("connect")}>
                Connect {connectCount > 0 && <span>🔴 {connectCount}</span>}
              </span> */}
            </div>
          </div>

          <div className="sidebar-content">
          
            {/* {activeTab === "all" && (() => {

              const pinned = allContacts.filter(c => pinnedContacts.includes(c.emails?.[0])).sort((a, b) => pinnedContacts.indexOf(b.emails?.[0]) - pinnedContacts.indexOf(a.emails?.[0]));
              const favorites = allContacts.filter(c => !pinnedContacts.includes(c.emails?.[0]) && favoriteContacts.includes(c.emails?.[0]) && !archivedContacts.includes(c.emails?.[0]));
              const normal = allContacts.filter(c => !pinnedContacts.includes(c.emails?.[0]) && !favoriteContacts.includes(c.emails?.[0]) && !archivedContacts.includes(c.emails?.[0]));
              return (<>{pinned.map(renderContact)}{favorites.map(renderContact)}{normal.map(renderContact)}</>);
            })()} */}

          {activeTab === "all" && (() => {
  const search = contactSearch.toLowerCase();

  // Build a map of conversations by email for quick lookup
  const convMap = {};
  conversations.forEach(cv => { convMap[cv.email] = cv; });

  // Build contact map
  const contactMap = {};
  contacts.forEach(c => { contactMap[c.emails?.[0]] = c; });

  // Merge: start with all contacts, overlay conversation data if exists
  const allEmails = new Set([
    ...contacts.map(c => c.emails?.[0]).filter(Boolean),
    ...conversations.map(cv => cv.email).filter(Boolean),
  ]);

  const merged = Array.from(allEmails)
    .map(email => {
      const contact = contactMap[email];
      const conv    = convMap[email];
      return {
        email,
        name:           contact?.name || conv?.name || email.split("@")[0],
        profileImg:     contact?.profileImageUrl || conv?.profileImageUrl || "",
        lastMessage:    conv?.lastMessage || "",
        lastTime:       conv?.lastTime || 0,
        isContact:      !!contact,
        hasConversation: !!conv,
      };
    })
    // Search filter
    .filter(item => {
      const name  = item.name.toLowerCase();
      const email = item.email.toLowerCase();
      return name.includes(search) || email.includes(search);
    })
    .sort((a, b) => {
      if (a.lastTime && b.lastTime) return b.lastTime - a.lastTime;
      if (a.lastTime) return -1;
      if (b.lastTime) return 1;
      return a.name.localeCompare(b.name);
    })
    // Status filter
    .filter(item => {
      if (statusFilter === "all") return true;
      return (chatStatusMap[item.email] || "open") === statusFilter;
    })
    // Sort: conversations with messages first (by time), then contacts with no messages
    
  // Split into pinned / favs / normal
  const pinned = merged.filter(item => pinnedContacts.includes(item.email));
  const favs   = merged.filter(item => !pinnedContacts.includes(item.email) && favoriteContacts.includes(item.email));
  const normal = merged.filter(item =>
    !pinnedContacts.includes(item.email) &&
    !favoriteContacts.includes(item.email) &&
    !archivedContacts.includes(item.email)
  );

  return [...pinned, ...favs, ...normal].map(item => {
    const status     = chatStatusMap[item.email] || "open";
    const isSelected = selectedContact?.emails?.[0] === item.email;

    return (
      <div
        key={item.email}
        className={`contact-item ${isSelected ? "active" : ""}`}
        onClick={() => {
          const syntheticContact = contactMap[item.email] || {
            id:             item.email,
            name:           item.name,
            emails:         [item.email],
            profileImageUrl: item.profileImg,
          };
          setSelectedContact(syntheticContact);
          setSelectedGroup(null);
          setOnlyAdmin(false);
          setUnreadMap(prev => ({ ...prev, [item.email]: 0 }));
          if (window.innerWidth < 768) setShowSidebar(false);
        }}
      >
        <div className="contact-avatar">
          {item.profileImg
            ? <img src={`${SERVER_URL}${item.profileImg}`} alt="profile" className="avatar-img" />
            : <div className="avatar-letter">{item.name.charAt(0).toUpperCase()}</div>
          }
        </div>

       <div
  style={{
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flex: 1,
    minWidth: 0,
    gap: 10,
  }}
>
  {/* Left Side */}
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minWidth: 0,
    }}
  >
    {/* Name */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        minWidth: 0,
      }}
    >
      <strong
        style={{
          color: "var(--text-primary)",
          fontSize: 14,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
        }}
      >
        {item.name}
      </strong>

      {pinnedContacts.includes(item.email) && <span>📌</span>}
      {favoriteContacts.includes(item.email) && <span>⭐</span>}

      {unreadMap[item.email] > 0 && (
        <span className="unread-badge">
          {unreadMap[item.email]}
        </span>
      )}
    </div>

    {/* Last message */}
    <div
      className="contact-sub"
      style={{
        marginTop: 3,
        display: "flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      <span
        className={`status-dot ${
          onlineUsers[String(item.email || "").toLowerCase()]
            ? "online"
            : "offline"
        }`}
      />

      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: 12,
          color: "#94a3b8",
          flex: 1,
        }}
      >
        {item.lastMessage || item.email}
      </span>
    </div>
  </div>

  {/* Right Side */}
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 6,
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 11, color: "#888" }}>
      {formatTime(item.lastTime)}
    </span>

   <div style={{ flexShrink: 0 }}>
            {status === "open"     && <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:"#06a03ff5",color:"#e9f3e9",fontSize:11,fontWeight:700,padding:"4px 9px",borderRadius:20,border:"1px solid rgba(37,99,235,0.3)" }}>📂 Open</span>}
            {status === "progress" && <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:"#1463e3",color:"#e9f3e9",fontSize:11,fontWeight:700,padding:"4px 9px",borderRadius:20,border:"1px solid rgba(245,158,11,0.3)" }}>🔄 Progress</span>}
            {status === "action"   && <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:"#c98f1f",color:"#e9f3e9",fontSize:11,fontWeight:700,padding:"4px 9px",borderRadius:20,border:"1px solid rgba(239,68,68,0.3)" }}>⚠️ Action</span>}
            {status === "closed"   && <span style={{ display:"inline-flex",alignItems:"center",gap:4,background:"#50545a",color:"#e9f3e9",fontSize:11,fontWeight:700,padding:"4px 9px",borderRadius:20,border:"1px solid rgba(34,197,94,0.3)" }}>✅ Closed</span>}
          </div>
  </div>
</div>
      </div>
    );
  });
})()}

            {activeTab === "unread" && (
              <>
                {unreadContacts.map(contact => renderContact(contact))}
                {unreadGroups.map(group => (
                  <div
                    key={`group-${group.id}`}
                    className={`contact-item ${selectedGroup?.id === group.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedGroup(group);
                      setSelectedContact(null);
                      setUnreadMap(prev => ({ ...prev, [`group:${group.id}`]: 0 }));
                      if (window.innerWidth < 768) setShowSidebar(false);
                    }}
                  >
                    <div className="contact-avatar">
                      {group.groupImage ? <img src={`${SERVER_URL}${group.groupImage}`} className="avatar-img" alt="group" /> : <div className="avatar-letter">{group.name.charAt(0).toUpperCase()}</div>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flex: 1, minWidth: 0, gap: 8 }}>
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <strong style={{ color: "var(--text-primary)", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>{group.name}</strong>
                          <span className="unread-badge">{unreadMap[`group:${group.id}`] || 0}</span>
                        </div>
                        <div className="contact-sub" style={{ marginTop: 3 }}>
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160, display: "inline-block", color: "#94a3b8", fontSize: 12 }}>Group message</span>
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: "#86efac" }}>👥 Group</span>
                    </div>
                  </div>
                ))}
              </>
            )}

            {activeTab === "groups" && (
              <>
                {groupContacts.map(group => (
                  <div key={group.id} className={`contact-item ${selectedGroup?.id === group.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedGroup(group);
                      setSelectedContact(null);
                      setUnreadMap(prev => ({ ...prev, [`group:${group.id}`]: 0 }));
                      if (window.innerWidth < 768) setShowSidebar(false);
                    }}>
                    <div className="contact-avatar">
                      {group.groupImage ? <img src={`${SERVER_URL}${group.groupImage}`} className="avatar-img" /> : <div className="avatar-letter">{group.name.charAt(0).toUpperCase()}</div>}
                    </div>
                    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flex: 1,
    minWidth: 0,
    gap: 10,
  }}
>
  <div style={{ flex: 1, minWidth: 0 }}>
    <strong
      style={{
        display: "block",
        color: "var(--text-primary)",
        fontSize: 14,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {group.name}
    </strong>

    <div
      style={{
        marginTop: 3,
        color: "#94a3b8",
        fontSize: 12,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {group.lastMessage || "Group message"}
    </div>
  </div>

  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      flexShrink: 0,
    }}
  >
    <span style={{ fontSize: 11, color: "#888" }}>
      {formatTime(group.lastTime)}
    </span>

    {unreadMap[`group:${group.id}`] > 0 && (
      <span className="unread-badge" style={{ marginTop: 5 }}>
        {unreadMap[`group:${group.id}`]}
      </span>
    )}
  </div>
</div>
                    {/* <div className="contact-info"><strong>{group.name}</strong></div> */}
                    {pinnedGroups.includes(String(group.id)) && (
  <span style={{ marginLeft: 5 }}>📌</span>
)}
                  </div>
                ))}
              </>
            )}

            {activeTab === "calls" && (
              <CallsTab
                callHistory={callHistory} loading={loadingCalls} currentUser={currentUser}
                contacts={contacts} groups={groups} onDelete={deleteCall} onClearAll={clearAllCalls}
                onCallBack={(call) => {
                  if (call.groupId) {
                    const group = groups.find(g => String(g.id) === String(call.groupId));
                    if (group) { if (call.type === "video") setVideoCall({ isGroup: true, group, type: "video" }); else setVoiceCall({ isGroup: true, group, type: "voice" }); }
                    return;
                  }
                  const other = call.caller === currentUser ? call.receiver : call.caller;
                  if (call.type === "video") setVideoCall({ remoteUser: other, isCaller: true, type: "video" });
                  else setVoiceCall({ remoteUser: other, isCaller: true, type: "voice" });
                }}
              />
            )}
{activeTab === "channels" && (
  <>

    {normalizeArray(channels).length > 0 && <div style={{padding: "8px 12px", fontSize: 12, color: "#94a3b8"}}>My Channels</div>}
   {normalizeArray(channels).map(ch => (
  <div key={ch.id} className={`contact-item ${selectedChannel?.id === ch.id ? "active" : ""}`}
    onClick={() => {
      setSelectedChannel(ch);
      setSelectedContact(null);
      setSelectedGroup(null);
      setUnreadMap(prev => ({ ...prev, [`channel:${ch.id}`]: 0 }));
      if (window.innerWidth < 768) setShowSidebar(false);   // ← add this
    }}>
    <div className="contact-avatar">
      {ch.channelImage ? <img src={`${SERVER_URL}${ch.channelImage}`} className="avatar-img" /> : <div className="avatar-letter">📢</div>}
    </div>
    <div>
      <strong>{ch.name}</strong>
      <div className="contact-sub">{ch.lastMessage || `${ch.memberCount} subscribers`}</div>
    </div>
  </div>
))}
   <div style={{padding: "8px 12px", fontSize: 12, color: "#94a3b8"}}>Discover</div>
{exploreChannels
  .filter(ec => !channels.some(c => c.id === ec.id))
  .map(ec => (
    <div key={ec.id} className="contact-item" style={{justifyContent: "space-between"}}>
      <div style={{display: "flex", alignItems: "center", gap: 10}}>
        <div className="contact-avatar">
          {ec.channelImage ? <img src={`${SERVER_URL}${ec.channelImage}`} className="avatar-img" /> : <div className="avatar-letter">📢</div>}
        </div>
        <div>
          <strong>{ec.name}</strong>
          <div className="contact-sub">{ec.description || `${ec.memberCount || 0} subscribers`}</div>
        </div>
      </div>
     <button
  style={{ background: "#1463e3", color: "#fff", border: "none", borderRadius: 16, padding: "5px 12px", fontSize: 12, cursor: "pointer" }}
  onClick={async () => {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    await fetch(`${SERVER_URL}/api/channels/${ec.id}/subscribe`, {
      method: "POST",
      headers: { Authorization: `Bearer ${parsed.token}` },
    });
    const joined = { ...ec, myRole: "subscriber", memberCount: (ec.memberCount || 0) + 1 };
    setChannels(prev => [...prev, joined]);
    setSelectedChannel(joined);
    setSelectedContact(null);
    setSelectedGroup(null);
    if (window.innerWidth < 768) setShowSidebar(false);
  }}
>
  Subscribe
</button>
    </div>
))}
{exploreChannels.filter(ec => !channels.some(c => c.id === ec.id)).length === 0 && (
  <div style={{padding: "10px 12px", fontSize: 12, color: "#cbd5e1"}}>No new channels to discover</div>
)}
  </>
)}
            {/* {activeTab === "connect" && (
              <div className="connect-list">
                {connectRequests.length === 0 && <div className="empty-state">No Requests</div>}
                {connectRequests.map(req => (
                  <div key={req.id} className="connect-card">
                    <div className="connect-left">
                      <div className="avatar-circle">{req.fromUser.charAt(0).toUpperCase()}</div>
                      <div className="connect-info">
                        <div className="connect-name">{req.fromUser.split("@")[0]}</div>
                        <div className="connect-email">{req.fromUser}</div>
                      </div>
                    </div>
                    <div className="connect-actions">
                      <button
  className="accept-btn"
  onClick={() => handleAccept(req)}
  disabled={accepting === req.id}
>
  {accepting === req.id ? "..." : "✔"}
</button>
                      <button className="reject-btn" onClick={() => handleReject(req)}>✖</button>
                    </div>
                  </div>
                ))}
              </div>
            )} */}

            {activeTab === "status" && (
              <div className="status-page">
                <div className="status-header">
                  <h5>Status</h5>
                  <button className="status-add-btn" onClick={(e) => { e.stopPropagation(); statusInputRef.current.click(); }}>+</button>
                </div>
                <div className="status-list" onClick={() => {
                  const myStatuses = groupedStatuses.find(u => u.userId === currentUser);
                  if (myStatuses) openStatus(myStatuses); else statusInputRef.current.click();
                }}>
                  <div className="status-row">
                    <div className="status-ring"><img src="/default-avatar.png" className="status-avatar" /></div>
                    <div className="status-info">
                      <strong>My Status</strong>
                      <span>Click to add status update</span>
                      {(() => {
                        const myStatuses = groupedStatuses.find(u => u.userId === currentUser);
                        const myTotalViews = (myStatuses?.statuses || []).reduce((sum, status) => sum + (statusViewCounts[status.id] || 0), 0);
                        return myTotalViews > 0 ? <small>{myTotalViews} view{myTotalViews === 1 ? "" : "s"}</small> : null;
                      })()}
                    </div>
                  </div>
                </div>
                <h4 className="status-section">Recent</h4>
                <div className="status-list">
                  {groupedStatuses.filter(user => user.userId !== currentUser).map(user => {
                    const firstStatus = user.statuses[0];
                    const totalViews = user.statuses.reduce((sum, status) => sum + (statusViewCounts[status.id] || 0), 0);
                    const imageUrl = firstStatus.mediaUrl ? `${SERVER_URL}${firstStatus.mediaUrl}` : "/default-status.png";
                    const name = user.userId.split("@")[0];
                    return (
                      <div key={user.userId} className="status-row" onClick={() => openStatus(user)}>
                        <div className="status-ring"><img src={imageUrl} className="status-avatar" /></div>
                        <div className="status-info">
                          <strong>{name}</strong>
                          <span>{new Date(firstStatus.createdAt || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                          {user.userId === currentUser ? <small>{totalViews} view{totalViews === 1 ? "" : "s"}</small> : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input type="file" multiple ref={statusInputRef} style={{ display: "none" }} onChange={(e) => uploadStatus(e.target.files[0])} />
              </div>
            )}

            {activeTab === "archive" && <>{allContacts.filter(c => archivedContacts.includes(c.emails?.[0])).map(renderContact)}</>}
            {activeTab === "favs" && <>{allContacts.filter(c => favoriteContacts.includes(c.emails?.[0])).map(contact => renderContact(contact))}</>}
          </div>
        </div>
      )}

      {(!showSidebar || window.innerWidth > 768) && (
        <div className="chat-main">
         {selectedContact || selectedGroup || selectedChannel ? (
            <>
              <div className="chat-header">
                <button className="mobileback" onClick={() => setShowSidebar(true)}>←</button>
                <div className="chat-user-info" onClick={() => {
                  if (selectedGroup) setShowGroupInfo(true);
                  else if (selectedContact) { fetchUserProfile(selectedContact.emails[0]); setShowProfile(true); }
                }} style={{ cursor: "pointer" }}>
                 <div className="chat-avatar">
  {selectedContact?.profileImageUrl ? <img src={`${SERVER_URL}${selectedContact.profileImageUrl}`} alt="avatar" /> :
    selectedGroup?.groupImage ? <img src={`${SERVER_URL}${selectedGroup.groupImage}`} alt="group" /> :
    selectedChannel?.channelImage ? <img src={`${SERVER_URL}${selectedChannel.channelImage}`} alt="channel" /> :
    selectedChannel ? <div className="avatar-letter">📢</div> :
    <div className="avatar-letter">{(selectedContact?.name || selectedGroup?.name || "U")[0].toUpperCase()}</div>}
</div>
                  <div className="chat-user-text">
                  <div className="chat-user-name">
  {selectedContact?.name || selectedContact?.emails?.[0] || selectedGroup?.name || selectedChannel?.name}
</div>
                    <div className="chat-user-status">
                      {selectedContact && (
                        <>
                          <span className={`status-dot ${onlineUsers[String(selectedContact?.emails?.[0] || "").toLowerCase()] ? "online" : "offline"}`} />
                          {onlineUsers[String(selectedContact?.emails?.[0] || "").toLowerCase()] ? "Online" : "Offline"}
                          <span className="chat-phone">📱 {selectedContact?.phone || ""}</span>
                        </>
                      )}
                      {selectedGroup && <span className="group-label">👥 Group Chat</span>}
                      {selectedChannel && <span className="channel-label">📢 Channel</span>}
                    </div>
                  </div>
                </div>
                <div className="chat-header-right">
                  {selectedContact && !isInContacts && !selectedChannel && (
    <button
      title="Add to contacts"
      onClick={async () => {
        const name = prompt("Save contact as:", selectedContact.name || selectedContact.emails?.[0]?.split("@")[0] || "");
        if (name === null) return; // cancelled
        try {
          const raw = localStorage.getItem(AUTH_STORAGE_KEY);
          const parsed = JSON.parse(raw);
          await fetch(`${SERVER_URL}/api/contacts`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
            body: JSON.stringify({
              contactId: selectedContact.emails?.[0],
              alias: name.trim()
            })
          });
          // Refresh conversations so name updates in sidebar
          loadConversations();
          alert(`✅ ${name || selectedContact.emails?.[0]} added to contacts`);
        } catch (err) {
          alert("Failed to add contact");
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: "rgba(37,99,235,0.15)",
        color: "#60a5fa",
        border: "1px solid rgba(37,99,235,0.3)",
        borderRadius: 20,
        padding: "5px 12px",
        fontSize: 10,
        fontWeight: 600,
        cursor: "pointer",
        whiteSpace: "nowrap"
      }}
    >
      ➕ Add
    </button>
  )}
              {!selectedChannel && (
               <>
                  <div className="mail-search-box mobilecss">
                    <input placeholder="Search messages..." value={searchText} onChange={(e) => { setSearchText(e.target.value); searchMessages(e.target.value); }} />
                  </div>
                  <button className="voice-call-btn" onClick={() => {
                    window.__callStartTime = Date.now();
                    if (selectedGroup) setVoiceCall({ isGroup: true, group: selectedGroup, type: "voice" });
                    else { const email = selectedContact?.emails?.[0]; if (!email) return; setVoiceCall({ remoteUser: email, isCaller: true, type: "voice" }); }
                  }}><FiPhone size={18} /></button>
                  <button className="video-call-btn" onClick={() => {
                    window.__callStartTime = Date.now();
                    if (selectedGroup) setVideoCall({ isGroup: true, group: selectedGroup, type: "video" });
                    else { const email = selectedContact?.emails?.[0]; if (!email) return; setVideoCall({ remoteUser: email, isCaller: true, type: "video" }); }
                  }}><FiVideo size={20} /></button>
                  <div className="header-menu-wrapper">
                    <button className="menu-btn" onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      if (menu?.type === "header") setMenu(null);
                      else setMenu({ type: "header", x: rect.right - 120, y: rect.bottom + 5 });
                    }}>⋮</button>
                  </div>
                  </>
                )}
                </div>
              </div>
{!selectedChannel && (
  <>
              <div className="status-bar">
                <div className="status-options">
                  <button className={`status-btn open ${currentStatus === "open" ? "active" : ""}`} onClick={() => handleStatusChange("open")}>📂 Open</button>
                  <button className={`status-btn progress ${currentStatus === "progress" ? "active" : ""}`} onClick={() => handleStatusChange("progress")}>🔄 In Progress</button>
                  <button className={`status-btn action ${currentStatus === "action" ? "active" : ""}`} onClick={() => handleStatusChange("action")}>⚠️ Action Req.</button>
                  <button className={`status-btn closed ${currentStatus === "closed" ? "active" : ""}`} onClick={() => handleStatusChange("closed")}>✅ Closed</button>
                </div>
              </div>
                </>
                )}

{filePreviews.length === 0 && (  
              <div className="chat-messages"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const files = Array.from(e.dataTransfer.files);
                  setFilePreviews(files.map(file => ({ file, url: URL.createObjectURL(file), type: file.type })));
                }}
              >
                

                {chatMessages.filter(msg =>
                  msg.type === "location" || isLocationText(msg.text) ||
                  (typeof msg.text === "string" ? msg.text.trim() : "") || msg.url || msg.attachmentUrl
                ).map(msg => (
                  <div
                    key={msg.id || msg.time}
                    ref={(el) => messageRefs.current[msg.id] = el}
                    className={`chat-bubble ${msg.isOwn ? "own" : "other"} ${highlightMsg === msg.id ? "highlight" : ""}`}
                    onContextMenu={(e) => handleRightClick(e, msg)}
                    onMouseEnter={() => setHoverMsgId(msg.id)}
                    onMouseLeave={() => setHoverMsgId(null)}
                  >
                    {hoverMsgId === msg.id && (
                      <div className="quick-reaction">
                        <span onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setReactionPopup({ msgId: msg.id, x: rect.left, y: rect.top - 50 });
                        }}>👍</span>
                      </div>
                    )}

                    <>
                      {selectedGroup && !msg.isOwn && (
                        <div className="group-sender-name">{msg.senderName || msg.sender}</div>
                      )}

                      {msg.type === "audio" && (
                        <audio controls className="chat-audio">
                          <source src={`${SERVER_URL}${msg.url || msg.attachmentUrl}`} type="audio/webm" />
                        </audio>
                      )}

                      {(msg.type === "location" || isLocationText(msg.text)) && (
                        <LocationBubble text={msg.text} isOwn={msg.isOwn} />
                      )}

                      {(msg.url || msg.attachmentUrl) && msg.type !== "audio" && msg.type !== "qr" && (
                        <div className="media-message">
                          {(msg.type === "image" || (msg.url || msg.attachmentUrl || "").match(/\.(jpg|jpeg|png|gif|webp)$/i)) ? (
                            msg.isOwn || downloadMode === "auto" ? (
                              <img src={`${SERVER_URL}${msg.url || msg.attachmentUrl}`} className="chat-image" alt="media" />
                            ) : (
                              <ManualMediaItem url={`${SERVER_URL}${msg.url || msg.attachmentUrl}`} type="image" />
                            )
                          ) : (
                            <a href={`${SERVER_URL}${msg.url || msg.attachmentUrl}`} target="_blank" rel="noreferrer">
                              📄 {msg.filename || msg.attachmentName || "Download file"}
                            </a>
                          )}
                          {msg.text && <div className="image-caption">{msg.text}</div>}
                        </div>
                      )}

                      {msg.replyTo && (
                        <div className="reply-bubble" onClick={() => { if (msg.replyId) scrollToMessage(msg.replyId); }} style={{ cursor: "pointer" }}>
                          ↩ {msg.replyTo}
                        </div>
                      )}

                      {msg.deleted ? (
                        <i style={{ opacity: 0.6 }}>This message was deleted</i>
                      ) : (
                        <>
                          {msg.type === "qr" && (msg.attachmentUrl || msg.url) && (
                            <div style={{
                              padding: 12,
                              background: "rgba(255,255,255,0.05)",
                              borderRadius: 8,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}>
                              <img 
                                src={(msg.attachmentUrl || msg.url).startsWith("data:") ? (msg.attachmentUrl || msg.url) : `${SERVER_URL}${msg.attachmentUrl || msg.url}`}
                                alt="QR Code" 
                                style={{
                                  width: 180,
                                  height: 180,
                                  borderRadius: 6,
                                  border: "2px solid #3b5998",
                                }}
                              />
                              <p style={{ color: "#cbd5e1", fontSize: 11, marginTop: 8, textAlign: "center" }}>
                                Scan to join group
                              </p>
                            </div>
                          )}
                          {msg.type === "text" && msg.text?.trim() && (
                            <div className="message-text">
                            {reactionPopup && (
                              <div className="reaction-popup" style={{
                                position: "fixed", top: reactionPopup.y, left: reactionPopup.x,
                                background: "#1f2937", borderRadius: 30, padding: "6px 10px",
                                display: "flex", gap: 10, zIndex: 9999,
                              }}>
                                {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((emoji) => (
                                  <span key={emoji} style={{ cursor: "pointer", fontSize: 18 }}
                                    onClick={() => { handleReaction(reactionPopup.msgId, emoji); setReactionPopup(null); }}>
                                    {emoji}
                                  </span>
                                ))}
                                <span style={{ fontSize: 18 }}>+</span>
                              </div>
                            )}
                            <span>{highlightText(msg.text, searchText)}</span>
                            <span className="message-meta">
                           
                              {formatTime(msg.createdAt || msg.time)}
                              {msg.forwardedFrom && (
                                <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4, fontStyle: "italic" }}>↪ Forwarded</div>
                              )}
                              {msg.isOwn && (
                                <span className={`tick ${msg.status || "sent"}`}>
                                  {(msg.status || "sent") === "sent" && "✔"}
                                  {[("delivered"), ("seen")].includes(msg.status || "") && "✔✔"}
                                </span>
                              )}
                            </span>
                          </div>
                            )}
                        </>
                      )}

                    </>
                    {msg.reaction && <div className="reaction-display">{msg.reaction}</div>}
                  </div>
                ))}

                <div ref={chatEndRef} />
              </div>
              ) }

              {replyMsg && (
                <div className="reply-preview">
                  <strong>Replying to:</strong>
                  <div className="reply-text">{replyMsg.text}</div>
                  <button className="reply-close" onClick={() => setReplyMsg(null)}>✕</button>
                </div>
              )}
              {showEmoji && (
                <div className="emoji-picker-box">
                  <EmojiPicker onEmojiClick={handleEmojiClick} />
                </div>
              )}

              {showMention && mentionList.length > 0 && (
                <div className="mention-box" role="listbox" aria-label="Mention suggestions">
                  {mentionList.slice(0, 8).map((c, idx) => (
                    <div
                      key={c.id || c.emails?.[0] || idx}
                      className="mention-item"
                      role="option"
                      tabIndex={0}
                      onClick={() => { handleSelectMention(c); setShowMention(false); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { handleSelectMention(c); setShowMention(false); } }}
                    >
                      {/* <div className="mention-avatar">
                        {c.profileImageUrl ? (
                          <img src={`${SERVER_URL}${c.profileImageUrl}`} alt={c.name || c.emails?.[0]} />
                        ) : (
                          <span className="mention-initial">{(c.name || c.emails?.[0] || "U")[0].toUpperCase()}</span>
                        )}
                      </div> */}
                      <div className="mention-meta">
                        <div className="mention-name">{c.name || c.emails?.[0]}</div>
                        {/* <div className="mention-sub">{c.emails?.[0]}</div> */}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isRestricted && (
                <div className="group-restriction" style={{ textAlign: "center", padding: "10px", color: "#fff", background: "#ef4444", borderRadius: "6px", margin: "10px", width: "30%" }}>
                  🔒 Only admins can send messages
                </div>
              )}
{filePreviews.length === 0 && (

              <div className="chat-input-container">
                {typingUser && (
                  <div className="typing-indicator">
                    <span className="typing-text">{selectedContact?.name || typingUser} typing</span>
                    <span className="typing-dots"><span></span><span></span><span></span></span>
                  </div>
                )}
                <div className="input-pill">
                  <div className="pill-left">
                    <span className={`pill-icon plus-icon ${showAttachMenu ? "plus-open" : ""}`} onClick={() => setShowAttachMenu(prev => !prev)} title="More">+</span>
                    <span className="pill-icon" onClick={() => setShowEmoji(prev => !prev)} title="Emoji">😊</span>
                  </div>

                  {showAttachMenu && (
                    <div className="attach-popup">
                      <div className="attach-popup-item" onClick={() => { fileInputRef.current.accept = "image/*"; fileInputRef.current.click(); setShowAttachMenu(false); }}>
                        <div className="attach-popup-icon" style={{ background: "#7c3aed" }}>
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></svg>
                        </div>
                        <span>Photos</span>
                      </div>
                      <div className="attach-popup-item" onClick={() => { fileInputRef.current.accept = "*/*"; fileInputRef.current.click(); setShowAttachMenu(false); }}>
                        <div className="attach-popup-icon" style={{ background: "#2563eb" }}>
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z" /></svg>
                        </div>
                        <span>Document</span>
                      </div>
                      <div className="attach-popup-item" onClick={() => { handleSendLocation(); setShowAttachMenu(false); }}>
                        <div className="attach-popup-icon" style={{ background: "#16a34a" }}>
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
                        </div>
                        <span>Location</span>
                      </div>
                      <div className="attach-popup-item" onClick={() => { fileInputRef.current.accept = "audio/*"; fileInputRef.current.click(); setShowAttachMenu(false); }}>
                        <div className="attach-popup-icon" style={{ background: "#dc2626" }}>
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3zm-1 13V8l6 4-6 4z" /></svg>
                        </div>
                        <span>Audio</span>
                      </div>
                      <div className="attach-popup-item" onClick={() => { fileInputRef.current.accept = "video/*"; fileInputRef.current.click(); setShowAttachMenu(false); }}>
                        <div className="attach-popup-icon" style={{ background: "#ea580c" }}>
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="white"><path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z" /></svg>
                        </div>
                        <span>Video</span>
                      </div>
                    </div>
                  )}

                  <textarea
                    className="chat-input-field"
                    rows={1}
                    disabled={isRestricted || isChannelRestricted}
                    value={filePreviews.length > 0 ? caption : chatInput}
                    placeholder={
                      isBlocked ? "You can't send message (blocked)" :
                        isRestricted ? "🔒 Only admin can send messages" :
                          isChannelRestricted ? "🔒 Only channel members can send messages" :
                            filePreviews.length > 0 ? "Add a caption..." : "Type a message..."
                    }
                    onChange={(e) => {
                      if (isRestricted || isChannelRestricted) return;
                      const value = e.target.value;
                      e.target.style.height = "auto";
                      const newHeight = Math.min(e.target.scrollHeight, 120);
                      e.target.style.height = (newHeight > 44 ? newHeight : 44) + "px";
                      if (filePreviews.length > 0) {
                        setCaption(value);
                      } else {
                        setChatInput(value);
                        if (value.startsWith("/") && !selectedGroup) {
                          const query = value.slice(1).toLowerCase();
                          const matches = quickReplies.filter(r => r.shortcut.toLowerCase().startsWith(query));
                          setQuickRepliesMatches(matches);
                          setShowQuickReplies(matches.length > 0);
                        } else { setShowQuickReplies(false); }
                        if (selectedGroup) {
                          const cursorPos = e.target.selectionStart;
                          const textBeforeCursor = value.slice(0, cursorPos);
                          const match = textBeforeCursor.match(/@(\w*)$/);
                          if (match) {
                            const query = match[1].toLowerCase();
                            setMentionList(contacts.filter(c => (c.name || "").toLowerCase().includes(query)));
                            setShowMention(true);
                          } else { setShowMention(false); }
                        }
                        if (socketRef.current && selectedContact?.emails?.[0]) {
                          socketRef.current.emit("typing", { to: selectedContact.emails[0], from: currentUser });
                        }
                      }
                    }}
                    onPaste={(e) => {
                      try {
                        const clipboard = e.clipboardData || window.clipboardData;
                        if (!clipboard) return;
                        const items = Array.from(clipboard.items || []);
                        const files = [];
                        for (const it of items) {
                          if (it.kind === 'file') {
                            const f = it.getAsFile();
                            if (f) files.push(f);
                          }
                        }
                        if (files.length) {
                          e.preventDefault();
                          setFilePreviews(files.map(file => ({ file, url: URL.createObjectURL(file), type: file.type })));
                          setChatInput("");
                          setCaption("");
                        }
                      } catch (err) {
                        console.error('Paste to chat failed', err);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (isRestricted || isChannelRestricted) return;
                      if (e.key === "Enter" && e.shiftKey) return;
                      if (e.key === "Enter") {
                        e.preventDefault();
                        if (filePreviews.length > 0) handleMultiFileSend();
                        else handleSend();
                        e.target.style.height = "auto";
                      }
                      if (socketRef.current && selectedContact?.emails?.[0]) {
                        socketRef.current.emit("stop_typing", { to: selectedContact.emails[0] });
                      }
                    }}
                  />

                  <div className="pill-right">
                    {(filePreviews.length > 0 || (filePreviews.length > 0 ? caption : chatInput).trim()) ? (
                      <button className="pill-send-btn" disabled={isRestricted || isChannelRestricted} onClick={() => {
                        if (isRestricted || isChannelRestricted ) return;
                        if (filePreviews.length > 0) handleMultiFileSend();
                        else handleSend();
                      }}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="white"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
                      </button>
                    ) : (
                      <span className={`pill-mic-btn ${recording ? "recording" : ""}`} onClick={() => recording ? stopRecording() : startRecording()} title="Voice message">
                        {recording ? (
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4zm0 2a2 2 0 0 0-2 2v6a2 2 0 0 0 4 0V5a2 2 0 0 0-2-2zm-7 8a7 7 0 0 0 14 0h2a9 9 0 0 1-8 8.94V22h-2v-2.06A9 9 0 0 1 3 11h2z" /></svg>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                <input type="file" ref={fileInputRef} style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setFilePreviews(files.map(file => ({ file, url: URL.createObjectURL(file), type: file.type })));
                  }}
                  multiple
                />
              </div>
              )}
            </>
          ) : (
            <div className="chat-empty">
              <h3>Welcome to Kriva Chat</h3>
              <p>Select a contact or group to start chatting</p>
            </div>

          )}
              {filePreviews.length > 0 && (
  <ImageEditor
    filePreviews={filePreviews}
    setFilePreviews={setFilePreviews}
    caption={caption}
    setCaption={setCaption}
    fileInputRef={fileInputRef}
    onClose={() => { setFilePreviews([]); setCaption(""); }}
    onSend={handleMultiFileSend}
  />
)}
        </div>
      )}

      {/* ── Modals ── */}
      {showCreateGroup && (
        <CreateGroupModal contacts={contacts} setShowCreateGroup={setShowCreateGroup} handleCreateGroup={handleCreateGroup} />
      )}

{showCreateChannel && (
  <CreateChannelModal
    onClose={() => setShowCreateChannel(false)}
    onCreate={async (name, description) => {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = JSON.parse(raw);
      const res = await fetch(`${SERVER_URL}/api/channels`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${parsed.token}` },
        body: JSON.stringify({ name, description }),
      });
      const data = await res.json();
      setChannels(prev => [...prev, { ...data, myRole: "admin", memberCount: 1 }]);
      setShowCreateChannel(false);
    }}
  />
)}
      {showGroupInfo && selectedGroup && (
  <GroupInfoPanel
    group={selectedGroup} currentUser={currentUser}
    contacts={contacts}
    onAddContact={async (email, alias) => {
      const name = window.prompt("Save contact as:", alias || email?.split("@")[0] || "");
      if (name === null) return;
      await addContact({ email, name: name.trim() || alias || email?.split("@")[0] || "" });
      await loadConversations();
    }}
    onClose={() => setShowGroupInfo(false)}
        onLeave={() => { setShowGroupInfo(false); setSelectedGroup(null); setGroups(prev => prev.filter(g => g.id !== selectedGroup.id)); }}
        onDelete={() => { setShowGroupInfo(false); setSelectedGroup(null); setGroups(prev => prev.filter(g => g.id !== selectedGroup.id)); }}
    onViewProfile={(email) => { fetchUserProfile(email); setShowGroupInfo(false); setShowProfile(true); }}
  />
      )}

   {showProfile && profileUser && (
  <ContactInfoPanel
    profileUser={profileUser}
    currentUser={currentUser}
    onClose={() => setShowProfile(false)}
    isInContacts={isInContacts}
    onChat={() => {
      const contactEmail = profileUser.userId;
      if (!contactEmail) return;
      const contact = contacts.find(c => c.emails?.[0] === contactEmail) || {
        id: contactEmail,
        name: profileUser.fullName || contactEmail.split("@")[0],
        emails: [contactEmail],
      };
      setSelectedContact(contact);
      setSelectedGroup(null);
      setShowProfile(false);
      if (window.innerWidth < 768) setShowSidebar(false);
    }}
    onMail={() => {
      if (!profileUser?.userId) return;
      if (typeof onOpenComposeModal === "function") {
        onOpenComposeModal(profileUser.userId);
      } else {
        window.location.href = `/kmail/compose?to=${encodeURIComponent(profileUser.userId)}`;
      }
    }}
    onCall={() => {
      if (!profileUser?.userId) return;
      setVoiceCall({ remoteUser: profileUser.userId, isCaller: true, type: "voice" });
      setShowProfile(false);
    }}
    onAddContact={async () => {
      const name = prompt("Save contact as:", profileUser.fullName || profileUser.userId?.split("@")[0] || "");
      if (name === null) return;
      const alias = name.trim();
      try {
        await addContact({ email: profileUser.userId, name: alias });
        loadConversations();
        setShowProfile(false);
        alert(`✅ ${alias || profileUser.userId} added to contacts`);
      } catch (err) {
        alert("Failed to add contact");
      }
    }}
  />
)}
      {/* ── WhatsApp-style Edit Message Modal ── */}
      {editingMsgId && (
        <EditMessageModal
          msg={chatMessages.find((m) => m.id === editingMsgId)}
          editingText={editingText}
          setEditingText={setEditingText}
          onConfirm={() => handleEditMessage(editingMsgId)}
          onClose={() => { setEditingMsgId(null); setEditingText(""); }}
        />
      )}

      {showStatusViewer && currentStatusUser && (
        <div className="status-viewer">
          <div className="status-viewer-header">
            <button className="status-back" onClick={() => setShowStatusViewer(false)}>←</button>
            {currentStatusUser?.userId === currentUser && (
              <button className="status-delete" onClick={() => { const statusId = currentStatusUser.statuses[statusIndex].id; deleteStatus(statusId); }}>🗑</button>
            )}
            <button className="status-close" onClick={() => setShowStatusViewer(false)}>✕</button>
          </div>
          <div className="status-progress">
            {currentStatusUser.statuses.map((s, i) => (
              <div className="progress-bar" key={i}>
                {i === statusIndex && <div key={statusIndex} className="progress-fill"></div>}
                {i < statusIndex && <div style={{ width: "100%", height: "100%", background: "white" }}></div>}
              </div>
            ))}
          </div>
          <img src={`${SERVER_URL}${currentStatusUser.statuses[statusIndex].mediaUrl}`} className="status-full" />
          {currentStatusUser?.userId === currentUser && (
            <div className="status-viewers-inline">
              <button
                type="button"
                className="status-viewers-toggle"
                onClick={() => setShowViewersPanel((prev) => !prev)}
              >
                👁 {viewers.length} view{viewers.length === 1 ? "" : "s"}
              </button>
              {showViewersPanel && (
                <div className="status-viewers-panel">
                  <div className="status-viewers-panel-header">Viewed by</div>
                  {viewers.length > 0 ? (
                    viewers.map((v) => (
                      <div key={v.id || v.viewerId || v.User?.userId} className="status-viewers-item">
                        <div className="status-viewers-user">
                          <span className="status-viewers-name">{v.User?.fullName || v.viewerId || "Unknown user"}</span>
                          <span className="status-viewers-email">{v.User?.userId || v.viewerId || "No email"}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="status-viewers-empty">No one has viewed this status yet.</div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="status-left" onClick={() => { if (statusIndex > 0) setStatusIndex(statusIndex - 1); }}></div>
          <div className="status-right" onClick={() => { if (statusIndex < currentStatusUser.statuses.length - 1) setStatusIndex(statusIndex + 1); else setShowStatusViewer(false); }}></div>
        </div>
      )}

      {showAddContact && (
        <div className="contacts-form-overlay">
          <div className="contacts-form">
            <h3 className="form-title">Add New Contact</h3>
            <form onSubmit={handleAddContact} className="form-body">
              <div className="input-group">
                <input type="text" placeholder=" " value={contactForm.name} onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))} />
                <label>Contact Name</label>
              </div>
              <div className="input-group">
                <input type="text" placeholder=" " value={contactForm.emails[0] || ""} onChange={(e) => setContactForm(prev => ({ ...prev, emails: [e.target.value] }))} />
                <label>Email</label>
              </div>
              <div className="form-buttons">
                <button type="submit" className="btn-primary">Add Contact</button>
                <button type="button" className="btn-secondary" onClick={() => setShowAddContact(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {menu && (
        <div className="message-menu" style={{ position: "fixed", top: menu.y, left: menu.x, background: "#0f172a", borderRadius: "6px", padding: "5px", zIndex: 99999 }}>
          {menu.type === "sidebar" && (
            <>
              <div className="menu-item" onClick={() => { setShowCreateGroup(true); setMenu(null); }}>👥 New Group</div>
              <div className="menu-item" onClick={() => { setShowCreateChannel(true); setMenu(null); }}>📢 New Channel</div>
              <div className="menu-item" onClick={() => { setShowProfile(true); setMenu(null); }}>⚙ Profile</div>
            </>
          )}

          {menu.type === "header" && (
            <>
              <div className="menu-item" onClick={() => { handleFavoriteContact(selectedContact.emails[0]); setMenu(null); }}>
                ⭐ {favoriteContacts.includes(selectedContact?.emails?.[0]) ? "Remove Favorites" : "Add Favorites"}
              </div>
              <div className="menu-item" onClick={() => {
  if (selectedGroup) {
    handlePin(`group:${selectedGroup.id}`);
  } else {
    handlePin(selectedContact?.emails?.[0]);
  }
}}>
                📌 {pinnedContacts.includes(selectedContact?.emails?.[0]) ? "Unpin" : "Pin"}
              </div>
              <div className="menu-item" onClick={() => handleArchive(selectedContact?.emails?.[0])}>
                📦 {archivedContacts.includes(selectedContact?.emails?.[0]) ? "Unarchive" : "Archive"}
              </div>
              <div className="menu-item" onClick={() => { handleClearChat(); setMenu(null); }}>🧹 Clear Chat</div>
              {isBlocked ? (
                <div className="menu-item" onClick={handleUnblockUser}>✅ Unblock User</div>
              ) : (
                <div className="menu-item" onClick={handleBlockUser}>🚫 Block User</div>
              )}
            </>
          )}

          {menu.type === "message" && (
            <>
              <div className="menu-item" onClick={() => { setReplyMsg(menu.message); setMenu(null); }}>↩ Reply</div>
              <div className="menu-item" onClick={() => { setForwardMsg(menu.message); setShowForwardModal(true); setMenu(null); }}>↪ Forward</div>
              {menu.message?.text?.trim() && (
                <div className="menu-item" onClick={() => { handleCopyMessage(menu.message.text); setMenu(null); }}>📋 Copy</div>
              )}
              {(menu.message?.url || menu.message?.attachmentUrl) && (
                <div className="menu-item" onClick={() => {
                  const fileUrl = `${SERVER_URL}${menu.message.url || menu.message.attachmentUrl}`;
                  const fileName = menu.message.filename || menu.message.attachmentName || "download";
                  const link = document.createElement("a");
                  link.href = fileUrl; link.download = fileName; link.target = "_blank";
                  document.body.appendChild(link); link.click(); document.body.removeChild(link);
                  setMenu(null);
                }}>⬇ Download</div>
              )}
              {menu.message?.isOwn && canEditOrDelete(menu.message) && (
                <>
                  <div className="menu-item" onClick={() => {
                    setEditingMsgId(menu.message.id);
                    setEditingText(menu.message.text);
                    setMenu(null);
                  }}>✏️ Edit</div>
                  <div className="menu-item" onClick={() => { handleDeleteMessage(menu.message.id); setMenu(null); }}>🗑 Delete</div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {showForwardModal && (
        <ForwardModal
          contacts={contacts} groups={groups} forwardMsg={forwardMsg} forwarding={forwarding}
          onSend={handleForward}
          onClose={() => { setShowForwardModal(false); setForwardMsg(null); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   CALLS TAB
───────────────────────────────────────────── */
function CallsTab({ callHistory, loading, currentUser, contacts, groups, onDelete, onClearAll, onCallBack }) {
  const [filter, setFilter] = React.useState("all");
  const filtered = callHistory.filter(c => {
    if (filter === "missed") return c.status === "missed";
    if (filter === "video") return c.type === "video";
    if (filter === "voice") return c.type === "voice";
    return true;
  });
  const formatDuration = (secs) => {
    if (!secs || secs < 1) return "";
    const m = Math.floor(secs / 60), s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };
  const formatCallTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts), now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (isToday) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString([], { day: "numeric", month: "short" });
  };
  const getContactName = (email) => {
    const c = contacts.find(c => c.emails?.[0] === email);
    return c?.name || email?.split("@")[0] || email;
  };
  const getCallIcon = (call, isCaller) => {
    if (call.status === "missed") return <FiPhoneMissed size={14} color="#ef4444" />;
    if (isCaller) return call.type === "video" ? <FiVideo size={14} color="#22c55e" /> : <FiPhone size={14} color="#22c55e" />;
    return <FiPhoneIncoming size={14} color="#3b82f6" />;
  };
  if (loading) return (
    <div style={cs.loadWrap}>
      <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#94a3b8", fontSize: 20 }} />
      <span style={{ color: "#94a3b8", fontSize: 13 }}>Loading calls…</span>
    </div>
  );
  return (
    <div style={cs.wrap}>
      <div style={cs.filterRow}>
        {["all", "missed", "voice", "video"].map(f => (
          <button key={f} style={{ ...cs.chip, ...(filter === f ? cs.chipActive : {}) }} onClick={() => setFilter(f)}>
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        {callHistory.length > 0 && (
          <button style={{ ...cs.chip, marginLeft: "auto", color: "#ef4444", borderColor: "#fecaca" }}
            onClick={() => window.confirm("Clear all call history?") && onClearAll()}>Clear All</button>
        )}
      </div>
      {filtered.length === 0 && (
        <div style={cs.empty}><FiPhoneOff size={32} color="#cbd5e1" /><p style={{ color: "#94a3b8", fontSize: 13, margin: "8px 0 0" }}>No call history</p></div>
      )}
      {filtered.map(call => {
        const isCaller = call.caller === currentUser;
        const other = isCaller ? call.receiver : call.caller;
        const name = call.groupName || getContactName(other);
        const isGroup = !!call.groupId;
        const isMissed = call.status === "missed";
        return (
          <div key={call.id} style={cs.item}>
            <div style={cs.avatar}>
              {isGroup ? <span style={{ fontSize: 18 }}>👥</span> : <span style={{ fontSize: 18, fontWeight: 700, color: "#64748b" }}>{name.charAt(0).toUpperCase()}</span>}
            </div>
            <div style={cs.info}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {getCallIcon(call, isCaller)}
                <span style={{ fontWeight: 600, fontSize: 14, color: isMissed ? "#ef4444" : "#1e293b" }}>{name}</span>
              </div>
              <div style={cs.meta}>
                <span>{isCaller ? "Outgoing" : "Incoming"}</span><span>·</span>
                <span>{call.type === "video" ? "📹 Video" : "📞 Voice"}</span>
                {call.duration > 0 && (<><span>·</span><span>{formatDuration(call.duration)}</span></>)}
              </div>
            </div>
            <div style={cs.right}>
              <span style={cs.time}>{formatCallTime(call.createdAt || call.startTime)}</span>
              <div style={cs.actions}>
                <button style={cs.actionBtn} title="Call back" onClick={() => onCallBack(call)}>
                  {call.type === "video" ? <FiVideo size={14} /> : <FiPhone size={14} />}
                </button>
                <button style={{ ...cs.actionBtn, color: "#ef4444" }} title="Delete" onClick={() => onDelete(call.id)}>
                  <i className="fa-solid fa-trash" style={{ fontSize: 11 }} />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const cs = {
  wrap: { display: "flex", flexDirection: "column", gap: 0 },
  loadWrap: { display: "flex", alignItems: "center", gap: 10, padding: 24, justifyContent: "center" },
  filterRow: { display: "flex", gap: 6, padding: "10px 12px", flexWrap: "wrap", borderBottom: "1px solid #f1f5f9" },
  chip: { border: "1px solid #e2e8f0", borderRadius: 20, padding: "3px 10px", fontSize: 11, cursor: "pointer", background: "#fff", color: "#64748b", fontWeight: 500 },
  chipActive: { background: "#1e40af", color: "#fff", border: "1px solid #1e40af" },
  empty: { display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 0" },
  item: { display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid #f8fafc", transition: "background 0.1s" },
  avatar: { width: 42, height: 42, borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  info: { flex: 1, minWidth: 0 },
  meta: { display: "flex", gap: 4, fontSize: 11, color: "#94a3b8", marginTop: 2, flexWrap: "wrap" },
  right: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 },
  time: { fontSize: 11, color: "#94a3b8" },
  actions: { display: "flex", gap: 4 },
  actionBtn: { border: "none", background: "transparent", cursor: "pointer", color: "#3b82f6", padding: "2px 4px", borderRadius: 4, display: "flex", alignItems: "center" },
};

/* ─────────────────────────────────────────────
   LOCATION BUBBLE
───────────────────────────────────────────── */
function LocationBubble({ text, isOwn }) {
  let lat = 0, lng = 0, isLive = false;
  try {
    const d = typeof text === "string" ? JSON.parse(text) : text;
    if (!d || typeof d.latitude !== "number") throw new Error("invalid");
    lat = d.latitude; lng = d.longitude; isLive = d.isLive;
  } catch { return <span style={{ fontSize: 12, opacity: 0.7 }}>📍 Location</span>; }

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
  const openMap = () => window.open(mapsUrl, "_blank", "noopener,noreferrer");

  return (
    <div style={{ minWidth: 220, maxWidth: 280 }}>
      <div onClick={openMap} style={{ borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer", position: "relative" }}>
        <div style={{ width: "100%", height: 130, background: "linear-gradient(135deg,#1e3a5f,#0f2540)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ width: 36, height: 36, background: "linear-gradient(135deg,#ef4444,#dc2626)", borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", boxShadow: "0 4px 12px rgba(239,68,68,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 12, height: 12, background: "#fff", borderRadius: "50%" }} />
            </div>
            <div style={{ width: 8, height: 4, background: "rgba(0,0,0,0.3)", borderRadius: "50%", margin: "2px auto 0", filter: "blur(2px)" }} />
          </div>
          <div style={{ position: "relative", zIndex: 1, fontSize: 11, color: "#93c5fd", fontFamily: "monospace", letterSpacing: "0.3px" }}>{lat.toFixed(4)}, {lng.toFixed(4)}</div>
          <div style={{ position: "absolute", bottom: 8, fontSize: 10, color: "rgba(255,255,255,0.5)", display: "flex", alignItems: "center", gap: 4, zIndex: 1 }}>
            <span>🗺️</span> Tap to open in Maps
          </div>
          {isLive && (
            <div style={{ position: "absolute", top: 8, right: 8, zIndex: 2, background: "rgba(239,68,68,0.9)", color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 4, boxShadow: "0 2px 8px rgba(239,68,68,0.4)" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "blink 1s infinite", display: "inline-block" }} />
              LIVE
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <button onClick={openMap} style={{ flex: 1, background: "rgba(37,99,235,0.85)", color: "#fff", border: "none", borderRadius: 6, padding: "6px 0", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
          🗺️ Open in Maps
        </button>
        <button onClick={() => navigator.clipboard?.writeText(`${lat},${lng}`)} title="Copy coordinates" style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 11, cursor: "pointer" }}>
          📋
        </button>
      </div>
    </div>
  );
}

export default Krivachat;
